import { loadAppMessageTransport, sendIndependentAppMessage, type AppMessageTransport } from "@/lib/app-messages";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Download, ExternalLink, LayoutTemplate, Loader2, MoreHorizontal, PanelLeftClose, PanelLeftOpen, Pencil, Plug, RefreshCw, Rocket, RotateCcw, Share2, Trash2, X } from "lucide-react";
import { AppIntegrationsDrawer, type AppIntegration } from "@/components/operating-systems/AppIntegrationsDrawer";
import { ShareOperatingSystemDialog } from "@/components/operating-systems/ShareOperatingSystemDialog";
import { resolveOsPermissionRemote } from "@/lib/os-permissions-remote";
import { canAlterOsSystem, canManageOsSharing, canReadOs } from "@/lib/os-permissions";
import { Header } from "@/components/Header";
import { RecoveryBoundary } from "@/components/RecoveryBoundary";
import { useAuth } from "@/components/auth/AuthContext";
import { shouldPauseAppPolling, shouldReloadAfterRecovery } from "@/components/auth/authRecoveryPolling";
import { clearAppComposerDraft, readAppComposerDraft, writeAppComposerDraft } from "@/components/auth/appSessionDrafts";
import {
  Base44MessagesPanel,
  type ProviderClarificationAnswer,
  type ProviderDialogAction,
} from "@/components/operating-systems/Base44MessagesPanel";
import type {
  AgenticFlowDebugDetail,
  AgenticFlowListResponse,
  AgenticFlowProductDetail,
} from "@/lib/agentic-flows";
import { isAgenticUiAvailableHere } from "@/lib/agentic-ui-availability";
import {
  parseProviderDialogDescription,
  type ProviderDialogDescription,
  type ProviderDialogFormValues,
} from "@/components/operating-systems/providerDialogDescription";
import {
  isStaleProviderMessageReplyError,
  latestProviderMessageRequestsReply,
} from "@/components/operating-systems/providerMessageReply";
import {
  currentAgenticFlowActivity,
  isAgenticFlowActivityEvent,
  isVisibleActivityEvent,
  isPostCompletionProviderClarification,
  latestWaitingProviderDialogEvent,
  latestWaitingProviderClarificationEvent,
  routerClarificationAssociationState,
} from "@/components/operating-systems/base44MessageVisibility";
import {
  OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT,
  useOperatingSystemPreviewCache,
  type OperatingSystemPreviewProviderLoadingDetail,
} from "@/components/operating-systems/OperatingSystemPreviewCacheContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ToastAction } from "@/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useDocumentPdf } from "@/hooks/useDocumentPdf";
import { useOperatingSystemProxyTicket } from "@/hooks/useOperatingSystemProxyTicket";
import { useRuntimeRecovery } from "@/hooks/useRuntimeRecovery";
import { useOperatingSystemScreenshotUrl } from "@/hooks/useOperatingSystemScreenshotUrl";
import { supabase } from "@/integrations/supabase/client";
import { recognizeAttachment } from "../../supabase/functions/_shared/documentFormats";
import {
  getSupabaseFunctionErrorDetails,
  getSupabaseFunctionErrorMessage,
  isSupabaseFunctionIdleTimeoutMessage,
} from "@/lib/supabase-function-errors";
import { isAppTemplateConversionEligible } from "@/lib/app-template-filters";
import { appRequestOutcome } from "@/lib/app-request-outcome";
import { isCurrentAppDetailLoad, shouldAutoOpenAppActivity } from "@/lib/app-detail-view";
import {
  OPERATING_SYSTEM_APP_SAFE_SELECT,
  OPERATING_SYSTEM_JOB_EVENT_SAFE_SELECT,
  OPERATING_SYSTEM_JOB_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT,
  OPERATING_SYSTEM_VISIBLE_HISTORY_FILTER,
  OperatingSystemApp,
  OperatingSystemJobEvent,
  OperatingSystemJob,
  OperatingSystemRuntimeCache,
  OperatingSystemRuntimeLifecycle,
  getOperatingSystemDescription,
  getOperatingSystemFeedbackLabel,
  getOperatingSystemPreviewDescriptor,
  getOperatingSystemRestartLabel,
  getOperatingSystemRestartOperation,
  openOperatingSystemRunInNewTab,
  getOperatingSystemRuntimeLifecycleFeedback,
  getOperatingSystemStatusLabel,
  getOperatingSystemStepLabel,
  getAppStatusDescriptor,
  getTemplateInstanceStatusDescriptor,
  getAppStatusFeedback,
  hasOperatingSystemEditableRuntime,
  hasOperatingSystemRunnableRuntime,
  isDebugCloneRuntimePending,
  isDebugCloneRuntimeValidated,
  isActiveOsJobStatus,
  isActiveOsStatus,
  isSupersededOperatingSystemUpdate,
  isSupportManagedAppRecovery,
  isOperatingSystemRuntimeRevisionPending,
  normalizeOperatingSystemDescription,
  OPERATING_SYSTEM_DESCRIPTION_MAX_LENGTH,
} from "@/lib/operating-systems";
import { rememberAppUpdateNotificationJob } from "@/lib/app-job-notifications";
import { createUuid } from "@/lib/uuid";
import { shouldResetPreviewRefreshAttempts } from "@/lib/operatingSystemPreviewCache";
import {
  AppTemplateActionError,
  appTemplateProperties,
  convertAppToTemplate,
  loadAppTemplateStates,
  loadSourceTemplateExportStatus,
  retryTemplateApp,
  type SourceTemplateExportStatus,
} from "@/lib/app-templates";

const PREVIEW_INITIAL_WARMUP_MS = 12000;
const PREVIEW_CACHED_WARMUP_MS = 0;
const PREVIEW_READY_REVEAL_DELAY_MS = 0;
const PREVIEW_REFRESH_MIN_INTERVAL_MS = 30_000;
const PREVIEW_REFRESH_MAX_ATTEMPTS = 20;
const PROVIDER_AUTHORIZATION_MAX_POLLS = 200;
// Provider activity is pulled server-side, so this is the user-visible
// freshness bound while an App update is running. `syncHistory` prevents
// overlapping pulls when a slow Provider response spans an interval.
const VISIBLE_HISTORY_SYNC_INTERVAL_MS = 5_000;
const VISIBLE_HISTORY_LIMIT = 120;
const HISTORY_PAGE_SIZE = 120;
const HISTORY_MAX_PAGES = 25;

// `public_metadata` arrives as loosely typed JSON from the database, so it is
// narrowed to a plain record before any property access.
function metaRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function optimisticAttachmentMetadata(files: File[]) {
  return files.map((file) => ({
    name: Array.from(file.name, (character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127 || character === "/" || character === "\\" ? " " : character;
    }).join("").replace(/\s+/g, " ").trim().slice(0, 160) || "attachment",
    size: file.size,
    content_type: file.type || "application/octet-stream",
  }));
}

function attachmentOnlyRequestContent(attachmentCount: number): string {
  return attachmentCount === 1 ? "Attached 1 file." : `Attached ${attachmentCount} files.`;
}

type RuntimePreviewRecoveryLog = {
  action: "refresh_ticket_and_preview" | "show_stuck_panel";
  attempt: number;
  cacheKey: string | null;
  osId: string | null;
  previewLoaded: boolean;
  providerLoading: boolean;
  reason: "runtime_ready_timeout" | "runtime_ready_timeout_after_recovery";
  runtimeSource: "cache" | "app" | null;
};

type RouterRequestReference = {
  replyToDecisionId?: string;
};

type PendingRouterRequest = {
  key: string;
  requestId: string;
};

type PendingProviderClarificationRequest = {
  key: string;
  requestId: string;
};

type PendingProviderDialogRequest = {
  key: string;
  requestId: string;
};

type PendingProviderMessageRequest = {
  key: string;
  requestId: string;
};

type AppActionMetadata = {
  type: "integration_setup" | "integration_feature_removal";
  integrationKey: string;
  displayName: string;
} | {
  type: "agentic_flow_modify";
  flowId: string;
  displayName: string;
} | {
  type: "agentic_flow_add";
  discoveryId: string;
  candidateId: string;
  displayName: string;
};

function isBase44PreviewOrigin(url: string): boolean {
  try {
    const u = new URL(url);
    return (
      u.protocol === "https:" &&
      (u.hostname === "base44.app" ||
        u.hostname.endsWith(".base44.app") ||
        u.hostname === "base44-preview.app" ||
        u.hostname.endsWith(".base44-preview.app") ||
        u.hostname === "app.base44.com")
    );
  } catch { return false; }
}

function isTrustedPreviewOrigin(origin: string, previewUrl?: string | null): boolean {
  if (isBase44PreviewOrigin(origin)) return true;
  if (!previewUrl) return false;
  try {
    return new URL(origin).origin === new URL(previewUrl).origin;
  } catch {
    return false;
  }
}

function logRuntimePreviewRecovery(details: RuntimePreviewRecoveryLog) {
  console.warn(`[os-runtime-preview-recovery] ${JSON.stringify({
    event: "os_runtime_preview_recovery",
    at: new Date().toISOString(),
    ...details,
  })}`);
}

function statusBadgeClass(status: string, templateStatus?: string | null, appClass?: string): string {
  if (templateStatus === "template") return "border-violet-300 bg-violet-50 text-violet-800";
  if (templateStatus === "preparing_template" || templateStatus === "retiring_template") {
    return "border-primary/30 bg-primary/10 text-primary";
  }
  if (templateStatus === "retire_failed") return "border-destructive/30 bg-destructive/10 text-destructive";
  return appClass ?? getAppStatusDescriptor({ app: { status: status as OperatingSystemApp["status"] } }).badgeClass;
}

function buildDocumentationDownloadFilename(app: OperatingSystemApp, documentTitle?: string | null): string {
  const baseName = (documentTitle || app.name || "Applyt App Documentation")
    .replace(/[^a-zA-Z0-9\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80)
    .replace(/\s/g, "-")
    .toLowerCase();

  return `${baseName || "applyt-documentation"}.pdf`;
}

function LiveWorkspaceConnectingOverlay({
  appName,
  hasSnapshot,
  providerLoading,
}: {
  appName: string;
  hasSnapshot: boolean;
  providerLoading?: boolean;
}) {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-background/95 p-6 text-center shadow-2xl backdrop-blur-md" role="status" aria-live="polite">
        <div className="mx-auto flex h-12 w-16 items-end justify-center gap-1.5 text-primary" aria-hidden="true">
          <span className="h-5 w-3 animate-pulse rounded-sm bg-primary/35" />
          <span className="h-9 w-3 animate-pulse rounded-sm bg-primary/70 [animation-delay:150ms]" />
          <span className="h-7 w-3 animate-pulse rounded-sm bg-primary/50 [animation-delay:300ms]" />
        </div>
        <h2 className="mt-4 text-base font-semibold text-foreground">Opening live workspace</h2>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {providerLoading
            ? `The live workspace is still starting. Applyt will keep trying and switch to ${appName} as soon as it responds.`
            : hasSnapshot
            ? `The background is a saved snapshot of ${appName}. Applyt will switch to the live App as soon as it connects.`
            : `Applyt is opening ${appName}. The live workspace may take a little while to appear.`}
        </p>
        <div className="mt-5 overflow-hidden rounded-full bg-primary/10" aria-hidden="true">
          <div className="h-1.5 w-1/2 animate-os-preview-progress rounded-full bg-primary" />
        </div>
      </div>
    </div>
  );
}

export default function OperatingSystemViewer() {
  const { osId } = useParams<{ osId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading, isAnonymous, authRecoveryState, runAuthenticatedRequest } = useAuth();
  const invokeAppFunction = useCallback(<T,>(name: string, body: unknown): Promise<T> =>
    runAuthenticatedRequest(async () => {
      const { data, error } = await supabase.functions.invoke<T>(name, { body });
      if (error) throw error;
      return data as T;
    }), [runAuthenticatedRequest]);
  const {
    activatePreview,
    isPreviewLoaded,
    isPreviewFrameVisible,
    isPreviewProviderLoading,
    isPreviewUnavailable,
    prewarmPreview,
    reloadPreview,
  } = useOperatingSystemPreviewCache();
  const [app, setApp] = useState<OperatingSystemApp | null>(null);
  const [runtimeCache, setRuntimeCache] = useState<OperatingSystemRuntimeCache | null>(null);
  const [runtimeLifecycle, setRuntimeLifecycle] = useState<OperatingSystemRuntimeLifecycle | null>(null);
  const [job, setJob] = useState<OperatingSystemJob | null>(null);
  const [historyEvents, setHistoryEvents] = useState<OperatingSystemJobEvent[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySyncing, setHistorySyncing] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [agenticFlowTarget, setAgenticFlowTarget] = useState<{ id: string; name: string } | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const loadedIdentityRef = useRef<string | null>(null);
  const currentIdentityRef = useRef<string | null>(null);
  const loadGenerationRef = useRef(0);
  const historyGenerationRef = useRef(0);
  const loadInFlightRef = useRef<{ identity: string; generation: number; promise: Promise<void> } | null>(null);
  const identity = `${osId ?? ""}:${user?.id ?? ""}:${isAnonymous}`;
  currentIdentityRef.current = identity;
  const [retrying, setRetrying] = useState(false);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [stopUpdateConfirmOpen, setStopUpdateConfirmOpen] = useState(false);
  const [stuckUpdateAction, setStuckUpdateAction] = useState<"stop" | "check_now" | null>(null);
  const restartSubmittingRef = useRef(false);
  const restartTargetIdRef = useRef<string | null>(null);
  const [quotaAction, setQuotaAction] = useState<"check_again" | "cancel_update" | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [templateConversionOpen, setTemplateConversionOpen] = useState(false);
  const [templateConverting, setTemplateConverting] = useState(false);
  const [sourceExportStatus, setSourceExportStatus] = useState<SourceTemplateExportStatus | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState("");
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const [effectivePermission, setEffectivePermission] = useState<"none" | "reader" | "editor" | "manager">("none");
  const [permissionResolved, setPermissionResolved] = useState(false);
  const [previewWarming, setPreviewWarming] = useState(false);
  const [previewTarget, setPreviewTarget] = useState<HTMLDivElement | null>(null);
  const [documentationDownloading, setDocumentationDownloading] = useState(false);
  const [changeRequest, setChangeRequest] = useState("");
  const changeRequestRef = useRef("");
  const [changeAttachments, setChangeAttachments] = useState<File[]>([]);
  const [optimisticChangeRequest, setOptimisticChangeRequest] = useState<OperatingSystemJobEvent | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changeSubmitting, setChangeSubmitting] = useState(false);
  const [authorizationWindowUrl, setAuthorizationWindowUrl] = useState<string | null>(null);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);
  const [integrationSummary, setIntegrationSummary] = useState<AppIntegration[] | null>(null);
  const [integrationStatusAvailable, setIntegrationStatusAvailable] = useState(false);
  const previousRecoveryStateRef = useRef(authRecoveryState);
  const previewWarmupTimerRef = useRef<number | null>(null);
  const previewRevealTimerRef = useRef<number | null>(null);
  const refreshAttemptsRef = useRef<number>(0);
  const refreshLastAtRef = useRef<number>(0);
  const refreshInFlightRef = useRef<boolean>(false);
  const historySyncInFlightRef = useRef<boolean>(false);
  const pendingRouterRequestRef = useRef<PendingRouterRequest | null>(null);
  const pendingAppMessageRequestIdsRef = useRef(new Map<string, string>());
  const pendingProviderClarificationRef = useRef<PendingProviderClarificationRequest | null>(null);
  const pendingProviderDialogRef = useRef<PendingProviderDialogRequest | null>(null);
  const pendingProviderMessageRef = useRef<PendingProviderMessageRequest | null>(null);
  const authorizationWindowRef = useRef<Window | null>(null);
  const moreActionsRef = useRef<HTMLDivElement>(null);
  const moreActionsMenuRef = useRef<HTMLDivElement>(null);
  const moreActionsTriggerRef = useRef<HTMLButtonElement>(null);
  const authorizationDialogEventIdRef = useRef<string | null>(null);
  const authorizationDialogKeyRef = useRef<string | null>(null);
  const authorizationJobIdRef = useRef<string | null>(null);
  const closeAuthorizationWindow = useCallback(() => {
    const authorizationWindow = authorizationWindowRef.current;
    authorizationWindowRef.current = null;
    try {
      authorizationWindow?.close();
    } catch {
      // The authorization window may already have closed itself.
    }
  }, []);
  const {
    documentPdf,
    generating: documentPdfGenerating,
    waitingForDocument: documentPdfWaitingForDocument,
  } = useDocumentPdf(
    app?.source_message_id ?? null,
    user?.id ?? null,
    { autoGenerate: true },
  );
  const documentPdfPreparing = documentPdfGenerating || (!!user?.id && documentPdfWaitingForDocument);

  const [messageTransport, setMessageTransport] = useState<AppMessageTransport | null>(null);
  const independentMessaging = messageTransport?.applicationId === app?.id && messageTransport?.enabled === true;

  const loadOperatingSystem = useCallback(async () => {
    if (!osId) return;
    if (authLoading) return;
    if (!user?.id || isAnonymous) {
      loadGenerationRef.current += 1;
      loadedIdentityRef.current = null;
      setApp(null);
      setRuntimeCache(null);
      setRuntimeLifecycle(null);
      setJob(null);
      setHistoryEvents([]);
      setInitialLoading(false);
      setLoadError(false);
      return;
    }
    const requestIdentity = `${osId}:${user.id}:${isAnonymous}`;
    if (loadInFlightRef.current?.identity === requestIdentity &&
        loadInFlightRef.current.generation === loadGenerationRef.current) return loadInFlightRef.current.promise;
    const generation = ++loadGenerationRef.current;
    const isCurrent = () => isCurrentAppDetailLoad(requestIdentity, generation, currentIdentityRef.current, loadGenerationRef.current);
    if (loadedIdentityRef.current !== requestIdentity) {
      setInitialLoading(true);
      setApp(null);
      setRuntimeCache(null);
      setRuntimeLifecycle(null);
      setJob(null);
      setHistoryEvents([]);
      setEffectivePermission("none");
      setPermissionResolved(false);
    }
    const promise = (async () => {
      let authorizedAppVerified = false;
      try {
      const { data: appRow, error: appError } = await supabase
        .from("operating_system_apps")
        .select(OPERATING_SYSTEM_APP_SAFE_SELECT)
        .eq("id", osId)
        .maybeSingle();
      if (appError) throw appError;
      if (!appRow) {
        if (!isCurrent()) return;
        setApp(null);
        setRuntimeCache(null);
        setRuntimeLifecycle(null);
        setJob(null);
        setHistoryEvents([]);
        setEffectivePermission("none");
        setPermissionResolved(false);
        loadedIdentityRef.current = requestIdentity;
        setLoadError(false);
        return;
      }

      const baseApp = appRow as unknown as OperatingSystemApp;
      let sanitizedProperties = {};
      try {
        sanitizedProperties = (await loadAppTemplateStates([baseApp.id])).get(baseApp.id) ?? {};
      } catch {
        // Snapshot flags are security-relevant; never assume an App is runnable.
        throw new Error("Unable to verify App metadata.");
      }
      const nextApp: OperatingSystemApp = {
        ...baseApp,
        properties: sanitizedProperties,
      };
      authorizedAppVerified = true;
      const [cacheResult, lifecycleResult, jobResult, transportResult] = await Promise.all([
        supabase
          .from("operating_system_runtime_cache")
          .select(OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT)
          .eq("operating_system_id", nextApp.id)
          .maybeSingle(),
        supabase
          .from("operating_system_runtime_lifecycle")
          .select(OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT)
          .eq("operating_system_id", nextApp.id)
          .maybeSingle(),
        nextApp.latest_job_id ? supabase
          .from("operating_system_jobs")
          .select(OPERATING_SYSTEM_JOB_SAFE_SELECT)
          .eq("id", nextApp.latest_job_id)
          .maybeSingle() : Promise.resolve({ data: null, error: null }),
        loadAppMessageTransport(nextApp.id),
      ]);
      const { data: cacheRow, error: cacheError } = cacheResult;
      // Optional diagnostics cannot suppress an authorized, connected App.
      if (cacheError) console.warn("App runtime cache could not be refreshed");
      const { data: lifecycleRow, error: lifecycleError } = lifecycleResult;
      if (lifecycleError) console.warn("App lifecycle could not be refreshed");
      const { data: jobRow, error: jobError } = jobResult;
      if (jobError) console.warn("App job could not be refreshed");
      if (!isCurrent()) return;
      setApp(nextApp);
      if (transportResult) setMessageTransport(transportResult);
      setRuntimeCache(previous => cacheError && previous?.operating_system_id === nextApp.id ? previous : (cacheRow || null) as unknown as OperatingSystemRuntimeCache | null);
      setRuntimeLifecycle(previous => lifecycleError && previous?.operating_system_id === nextApp.id ? previous : (lifecycleRow || null) as unknown as OperatingSystemRuntimeLifecycle | null);
      setJob((jobRow || null) as unknown as OperatingSystemJob | null);
      loadedIdentityRef.current = requestIdentity;
      setLoadError(false);
      } catch {
        if (isCurrent()) {
          if (!authorizedAppVerified) {
            setApp(null);
            setRuntimeCache(null);
            setRuntimeLifecycle(null);
            setJob(null);
            setHistoryEvents([]);
            setEffectivePermission("none");
            setPermissionResolved(false);
            loadedIdentityRef.current = null;
          }
          setLoadError(true);
        }
      } finally {
        if (isCurrent()) setInitialLoading(false);
        if (loadInFlightRef.current?.promise === promise) loadInFlightRef.current = null;
      }
    })();
    loadInFlightRef.current = { identity: requestIdentity, generation, promise };
    return promise;
  }, [authLoading, isAnonymous, osId, user?.id]);

  useEffect(() => () => {
    loadGenerationRef.current += 1;
    historyGenerationRef.current += 1;
  }, [identity]);

  const loadHistory = useCallback(async (targetOsId?: string | null) => {
    const id = targetOsId ?? osId;
    if (!id || authLoading || !user?.id || isAnonymous) return;
    const requestIdentity = `${id}:${user.id}:${isAnonymous}`;
    if (currentIdentityRef.current !== requestIdentity) return;
    const generation = ++historyGenerationRef.current;
    const isCurrent = () => currentIdentityRef.current === requestIdentity && historyGenerationRef.current === generation;

    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const visibleEvents: OperatingSystemJobEvent[] = [];
      for (let page = 0; page < HISTORY_MAX_PAGES && visibleEvents.length < VISIBLE_HISTORY_LIMIT; page += 1) {
        const from = page * HISTORY_PAGE_SIZE;
        const { data, error } = await supabase
          .from("operating_system_job_events")
          .select(OPERATING_SYSTEM_JOB_EVENT_SAFE_SELECT)
          .eq("operating_system_id", id)
          .or(OPERATING_SYSTEM_VISIBLE_HISTORY_FILTER)
          .order("observed_at", { ascending: false })
          .order("created_at", { ascending: false })
          .range(from, from + HISTORY_PAGE_SIZE - 1);
        if (error) throw error;
        const rows = (data || []) as unknown as OperatingSystemJobEvent[];
        visibleEvents.push(...rows.filter(isVisibleActivityEvent));
        if (rows.length < HISTORY_PAGE_SIZE) break;
      }
      if (isCurrent()) {
        const nextEvents = visibleEvents.slice(0, VISIBLE_HISTORY_LIMIT);
        const confirmedRequestIds = new Set(nextEvents.flatMap((event) => {
          const metadata = metaRecord(event.public_metadata);
          return metadata.app_message_receipt_id && metadata.delivery_state === "accepted" &&
            typeof metadata.request_id === "string" ? [metadata.request_id] : [];
        }));
        for (const [key, requestId] of pendingAppMessageRequestIdsRef.current) {
          if (confirmedRequestIds.has(requestId)) pendingAppMessageRequestIdsRef.current.delete(key);
        }
        setHistoryEvents(nextEvents);
      }
    } catch {
      if (isCurrent()) setHistoryError("Unable to load App activity.");
    } finally {
      if (isCurrent()) setHistoryLoading(false);
    }
  }, [authLoading, isAnonymous, osId, user?.id]);

  useEffect(() => {
    void loadOperatingSystem();
  }, [loadOperatingSystem]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.id || isAnonymous) {
      setIsPlatformAdmin(false);
      return () => { cancelled = true; };
    }
    void supabase
      .from("profiles")
      .select("is_admin")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn("Unable to resolve platform administrator status:", error.message);
          setIsPlatformAdmin(false);
          return;
        }
        setIsPlatformAdmin(data?.is_admin === true);
      });
    return () => { cancelled = true; };
  }, [isAnonymous, user?.id]);

  useEffect(() => {
    closeAuthorizationWindow();
    authorizationDialogEventIdRef.current = null;
    authorizationDialogKeyRef.current = null;
    authorizationJobIdRef.current = null;
    setAuthorizationWindowUrl(null);
    setEditPanelOpen(false);
    setDescriptionEditing(false);
    setDescriptionValue("");
    setTemplateConversionOpen(false);
    setOptimisticChangeRequest(null);
  }, [closeAuthorizationWindow, osId]);

  useEffect(() => () => closeAuthorizationWindow(), [closeAuthorizationWindow]);

  useEffect(() => {
    if (!moreActionsOpen) return;

    const closeOnOutsidePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !moreActionsRef.current?.contains(event.target)) {
        setMoreActionsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setMoreActionsOpen(false);
      moreActionsTriggerRef.current?.focus();
    };
    const closeOnWindowBlur = () => {
      // Pointer events inside the embedded App do not bubble to this document.
      // Its frame takes focus instead, so close the header menu at that boundary.
      setMoreActionsOpen(false);
    };

    document.addEventListener("pointerdown", closeOnOutsidePointerDown, true);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("blur", closeOnWindowBlur);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointerDown, true);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("blur", closeOnWindowBlur);
    };
  }, [moreActionsOpen]);

  useEffect(() => {
    const dialogEventId = authorizationDialogEventIdRef.current;
    const dialogKey = authorizationDialogKeyRef.current;
    const jobId = authorizationJobIdRef.current;
    if (!authorizationWindowUrl || (!dialogEventId && !dialogKey)) return;
    const resolved = historyEvents.some((event) => {
      const isSameDialog = event.id === dialogEventId || (
        !!dialogKey && metaRecord(event.public_metadata).dialog_key === dialogKey &&
        (!jobId || event.job_id === jobId)
      );
      if (!isSameDialog) return false;
      const dialogState = metaRecord(event.public_metadata).dialog_state;
      return dialogState === "answered" || dialogState === "dismissed";
    });
    if (!resolved) return;
    authorizationDialogEventIdRef.current = null;
    authorizationDialogKeyRef.current = null;
    authorizationJobIdRef.current = null;
    setAuthorizationWindowUrl(null);
    pendingProviderDialogRef.current = null;
    closeAuthorizationWindow();
    setChangeSubmitting(false);
  }, [authorizationWindowUrl, closeAuthorizationWindow, historyEvents]);

  useEffect(() => {
    if (!app?.id) return;
    void loadHistory(app.id);
  }, [app?.id, loadHistory]);

  // Provider history is persisted by the job worker. Subscribe to those rows
  // so the activity panel receives a builder reply as soon as it is captured,
  // instead of waiting for the next polling pass or a page reload.
  useEffect(() => {
    if (!app?.id || !user?.id || isAnonymous) return;

    const channel = supabase
      .channel(`operating-system-history:${app.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "operating_system_job_events",
          filter: `operating_system_id=eq.${app.id}`,
        },
        (payload) => {
          if (currentIdentityRef.current !== `${app.id}:${user.id}:${isAnonymous}`) return;
          const row = payload.new as Record<string, unknown>;
          const nextEvent = {
            id: row.id,
            operating_system_id: row.operating_system_id,
            job_id: row.job_id,
            user_id: row.user_id,
            source: row.source,
            event_type: row.event_type,
            role: row.role,
            content: row.content,
            public_metadata: row.public_metadata,
            observed_at: row.observed_at,
            created_at: row.created_at,
          } as OperatingSystemJobEvent;
          if (!nextEvent.id || !isVisibleActivityEvent(nextEvent)) return;
          setHistoryEvents((current) => [
            nextEvent,
            ...current.filter((event) => event.id !== nextEvent.id),
          ].slice(0, VISIBLE_HISTORY_LIMIT));
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [app?.id, isAnonymous, user?.id]);

  const historicalUpdateChecking = job?.job_type === "update" &&
    job.provider_status === "verifying_previous_update" && job.status !== "completed";
  const historicalUpdateReview = job?.job_type === "update" &&
    job.current_step === "app_recovery_review" && job.provider_status === "review_required";
  const staleSupersededUpdate = isSupersededOperatingSystemUpdate(app, job);
  const heldLatestUpdate = !!job && job.id === app?.latest_job_id && isSupportManagedAppRecovery(job);
  const active = !!app && !staleSupersededUpdate && !heldLatestUpdate &&
    (isActiveOsStatus(app.status) || isActiveOsJobStatus(job?.status));
  const stuckUpdate = !!app && !!job && app.user_id === user?.id &&
    app.latest_job_id === job.id && app.status === "updating" &&
    job.job_type === "update" && isActiveOsJobStatus(job.status) &&
    Number.isFinite(Date.parse(job.created_at)) &&
    Date.now() - Date.parse(job.created_at) >= 15 * 60_000 &&
    runtimeCache?.last_ready_at != null && runtimeCache.last_ready_state === "ready" &&
    runtimeCache.last_ready_version === app.version && !!app.last_ready_source_message_id &&
    !!app.base44_app_url && !!app.base44_app_id;
  const updatePartial = metaRecord(job?.partial_result_json);
  const stopBeforeSend = stuckUpdate && !!job &&
    ["starting_os_build", "preparing_os_build", "preparing_required_app_capability"].includes(job.current_step) &&
    !job.provider_status && app?.base44_provider_status === "ready" && !job.external_job_id &&
    !Object.prototype.hasOwnProperty.call(updatePartial, "provider_send_intent") &&
    !Object.prototype.hasOwnProperty.call(updatePartial, "provider_command_started_at") &&
    !Object.prototype.hasOwnProperty.call(updatePartial, "recovery_candidate");
  const runtimeRevisionPending = isOperatingSystemRuntimeRevisionPending(app, runtimeCache);
  const canManageApp = canAlterOsSystem(effectivePermission);
  const canViewSourceExport = !!app && (app.user_id === user?.id || isPlatformAdmin);
  useEffect(() => {
    if (!app?.id || !canViewSourceExport) {
      setSourceExportStatus(null);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      void loadSourceTemplateExportStatus(app.id).then((status) => {
        if (!cancelled) setSourceExportStatus(status);
      }).catch(() => {
        // App loading remains available if the separate Template status lookup fails.
      });
    };
    refresh();
    return () => { cancelled = true; };
  }, [app?.id, canViewSourceExport]);
  useEffect(() => {
    if (!app?.id || sourceExportStatus?.app_id !== app.id ||
      !["queued", "running", "waiting_external_api"].includes(sourceExportStatus.status ?? "")) return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void loadSourceTemplateExportStatus(app.id).then((status) => {
        if (!cancelled) setSourceExportStatus(status);
      }).catch(() => {});
    }, 15_000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [app?.id, sourceExportStatus?.app_id, sourceExportStatus?.status]);
  const canManageSharing = canManageOsSharing(effectivePermission);
  const integrationAttentionCount = integrationStatusAvailable
    ? (integrationSummary ?? []).filter((item) => item.state === "action_needed" || item.state === "disconnected").length
    : null;
  const handleIntegrationSummary = useCallback((items: AppIntegration[] | null, available: boolean) => {
    setIntegrationSummary(items);
    setIntegrationStatusAvailable(available);
  }, []);
  const canView = canReadOs(effectivePermission);

  useEffect(() => {
    let cancelled = false;
    if (!app?.id || !user?.id) {
      setEffectivePermission("none");
      setPermissionResolved(Boolean(app?.id));
      return () => { cancelled = true; };
    }
    setPermissionResolved(false);
    void resolveOsPermissionRemote(app.id, user.id).then((level) => {
      if (cancelled) return;
      setEffectivePermission(level);
      setPermissionResolved(true);
    });
    return () => { cancelled = true; };
  }, [app?.id, user?.id]);

  // Dialog responses are accepted only while their job is active. Do not turn
  // a historical waiting snapshot from a failed/completed update into a live
  // action that the server will reject.
  const latestWaitingProviderDialog = job &&
    job.id === app?.latest_job_id &&
    isActiveOsJobStatus(job.status)
    ? latestWaitingProviderDialogEvent(historyEvents, app?.latest_job_id)
    : null;
  const failedAuthorizationDialog = job?.status === "failed"
    ? latestWaitingProviderDialogEvent(historyEvents, app?.latest_job_id)
    : null;
  const failedAuthorizationRetryAvailable =
    metaRecord(metaRecord(failedAuthorizationDialog?.public_metadata).dialog).kind === "oauth";

  useEffect(() => {
    const jobType = job?.job_type ?? (appTemplateProperties(app?.properties).template_instance ? "template_instance" : undefined);
    if (shouldAutoOpenAppActivity(active, jobType, !!latestWaitingProviderDialog, failedAuthorizationRetryAvailable) && app?.id === osId && canManageApp) {
      setEditPanelOpen(true);
    }
  }, [active, app?.id, app?.properties, canManageApp, failedAuthorizationRetryAvailable, job?.job_type, latestWaitingProviderDialog, osId]);

  const syncHistory = useCallback(async ({ userInitiated = false }: { userInitiated?: boolean } = {}) => {
    if (!app?.id || !canManageApp) return;
    if (document.visibilityState === "hidden") return;
    if (historySyncInFlightRef.current) return;

    historySyncInFlightRef.current = true;
    if (userInitiated) setHistorySyncing(true);
    setHistoryError(null);
    try {
      await invokeAppFunction("sync-os-history", { operatingSystemId: app.id, conversationOnly: true });
      await loadHistory(app.id);
      await loadOperatingSystem();
    } catch {
      await loadHistory(app.id);
      setHistoryError("Unable to refresh App activity.");
    } finally {
      historySyncInFlightRef.current = false;
      if (userInitiated) setHistorySyncing(false);
    }
  }, [app?.id, canManageApp, invokeAppFunction, loadHistory, loadOperatingSystem]);

  useEffect(() => {
    if ((!active && !runtimeRevisionPending) || shouldPauseAppPolling(authRecoveryState)) return;
    if (runtimeRevisionPending && document.visibilityState === "visible") void loadOperatingSystem();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      void loadOperatingSystem();
      if (active && app?.id) void loadHistory(app.id);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [active, app?.id, authRecoveryState, loadHistory, loadOperatingSystem, runtimeRevisionPending]);

  useEffect(() => {
    if (!app?.id || !canManageApp || shouldPauseAppPolling(authRecoveryState)) return;
    const synchronizeVisiblePage = () => {
      if (document.visibilityState === "visible") void syncHistory();
    };
    const onVisibilityChange = () => synchronizeVisiblePage();
    const onFocus = () => synchronizeVisiblePage();

    synchronizeVisiblePage();
    const timer = window.setInterval(() => {
      synchronizeVisiblePage();
    }, VISIBLE_HISTORY_SYNC_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
    };
  }, [app?.id, authRecoveryState, canManageApp, syncHistory]);

  useEffect(() => {
    const previous = previousRecoveryStateRef.current;
    previousRecoveryStateRef.current = authRecoveryState;
    if (!shouldReloadAfterRecovery(previous, authRecoveryState) || !app?.id) return;
    // Resume from authoritative state; do not re-submit any pending user action.
    void Promise.all([loadOperatingSystem(), loadHistory(app.id)]).then(() => { void syncHistory(); });
  }, [app?.id, authRecoveryState, loadHistory, loadOperatingSystem, syncHistory]);

  const isTemplateSnapshot = appTemplateProperties(app?.properties).template?.snapshot === true;
  const runtimeAvailable = !isTemplateSnapshot && hasOperatingSystemRunnableRuntime(app, runtimeCache, runtimeLifecycle);
  const canOpenRunPage = runtimeAvailable;
  const {
    ticket: proxyTicket,
    loading: proxyTicketLoading,
    error: proxyTicketError,
    refreshTicket,
  } = useOperatingSystemProxyTicket(app?.id, runtimeAvailable && !!user?.id && !isAnonymous, app?.version);
  const previewDescriptor = useMemo(
    () => getOperatingSystemPreviewDescriptor(app, runtimeCache, proxyTicket),
    [app, proxyTicket, runtimeCache],
  );
  const openUrl = previewDescriptor?.url || "";
  const previewCacheKey = previewDescriptor?.cacheKey || null;
  const previewLoaded = isPreviewLoaded(previewDescriptor);
  const previewFrameVisible = isPreviewFrameVisible(previewDescriptor);
  const previewProviderLoading = isPreviewProviderLoading(previewDescriptor);
  const previewUnavailable = isPreviewUnavailable(previewDescriptor) && !previewFrameVisible;
  const previewWarmupMs = previewLoaded ? PREVIEW_CACHED_WARMUP_MS : PREVIEW_INITIAL_WARMUP_MS;
  const templateSnapshotImageUrl = useOperatingSystemScreenshotUrl(
    isTemplateSnapshot ? runtimeCache?.screenshot_url ?? null : null,
  );

  const clearPreviewTimers = useCallback(() => {
    if (previewWarmupTimerRef.current !== null) {
      window.clearTimeout(previewWarmupTimerRef.current);
      previewWarmupTimerRef.current = null;
    }
    if (previewRevealTimerRef.current !== null) {
      window.clearTimeout(previewRevealTimerRef.current);
      previewRevealTimerRef.current = null;
    }
  }, []);

  const showPreviewWarmup = useCallback((durationMs: number | null = null) => {
    setPreviewWarming(true);
    if (previewWarmupTimerRef.current !== null) {
      window.clearTimeout(previewWarmupTimerRef.current);
      previewWarmupTimerRef.current = null;
    }
    if (previewRevealTimerRef.current !== null) {
      window.clearTimeout(previewRevealTimerRef.current);
      previewRevealTimerRef.current = null;
    }
    if (durationMs === null) return;
    previewWarmupTimerRef.current = window.setTimeout(() => {
      previewWarmupTimerRef.current = null;
      setPreviewWarming(false);
    }, durationMs);
  }, []);

  const refreshPreviewUrl = useCallback(async (options: {
    force?: boolean;
    providerLoading?: boolean;
    recoveryAttempt?: number;
    recoveryReason?: RuntimePreviewRecoveryLog["reason"];
  } = {}): Promise<void> => {
    if (!app?.id) return;
    if (refreshInFlightRef.current) return;
    const now = Date.now();
    if (options.providerLoading && !refreshLastAtRef.current && !options.force) {
      refreshLastAtRef.current = now;
      return;
    }
    if (!options.force && now - refreshLastAtRef.current < PREVIEW_REFRESH_MIN_INTERVAL_MS) return;
    if (!options.force && refreshAttemptsRef.current >= PREVIEW_REFRESH_MAX_ATTEMPTS) return;
    refreshInFlightRef.current = true;
    refreshLastAtRef.current = now;
    refreshAttemptsRef.current += 1;
    try {
      const body: {
        operatingSystemId: string;
        diagnostic?: {
          cacheKey: string | null;
          recoveryAttempt: number | null;
          reason: RuntimePreviewRecoveryLog["reason"];
          source: "OperatingSystemViewer";
        };
      } = { operatingSystemId: app.id };
      if (options.recoveryReason) {
        body.diagnostic = {
          cacheKey: previewCacheKey,
          recoveryAttempt: options.recoveryAttempt ?? null,
          reason: options.recoveryReason,
          source: "OperatingSystemViewer",
        };
      }
      let status: string | undefined;
      try {
        const data = await invokeAppFunction<{ status?: string }>("refresh-os-preview", body);
        status = data?.status;
      } catch (error) {
        console.warn("Failed to refresh App preview; renewing runtime ticket:", error);
      }
      await loadOperatingSystem();
      const nextTicket = await refreshTicket();
      if (nextTicket && previewDescriptor?.canEmbed && nextTicket.proxyUrl !== previewDescriptor.url) {
        reloadPreview({
          ...previewDescriptor,
          url: nextTicket.proxyUrl,
          embedUrl: nextTicket.proxyUrl,
          proxyExpiresAt: nextTicket.expiresAt,
        });
      }
      if (previewLoaded && shouldResetPreviewRefreshAttempts(status, previewLoaded)) {
        refreshAttemptsRef.current = 0;
      }
    } catch (err) {
      console.warn("refresh-os-preview failed:", err);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [app?.id, invokeAppFunction, loadOperatingSystem, previewCacheKey, previewDescriptor, previewLoaded, refreshTicket, reloadPreview]);

  // Listen for the provider's "Preview is starting up" handshake and refresh our URL.
  useEffect(() => {
    if (!app?.id || !previewDescriptor) return undefined;
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: unknown } | null;
      if (!data || typeof data !== "object" || data.type !== "preview:requestRefresh") return;
      if (!isTrustedPreviewOrigin(event.origin, previewDescriptor.url)) return;
      void refreshPreviewUrl({ providerLoading: true });
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [app?.id, previewDescriptor, refreshPreviewUrl]);

  useEffect(() => {
    if (!app?.id || !previewDescriptor?.cacheKey) return undefined;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<OperatingSystemPreviewProviderLoadingDetail>).detail;
      if (
        detail?.osId !== app.id ||
        detail.cacheKey !== previewDescriptor.cacheKey
      ) {
        return;
      }
      void refreshPreviewUrl({ providerLoading: true });
    };
    window.addEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handler);
    return () => window.removeEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handler);
  }, [app?.id, previewDescriptor?.cacheKey, refreshPreviewUrl]);

  const previewRecoveryKey = runtimeAvailable && app?.id ? `${app.id}:${app.version ?? 0}` : null;
  const previewRecoveryKeyRef = useRef(previewRecoveryKey);
  previewRecoveryKeyRef.current = previewRecoveryKey;
  const previewRecovery = useRuntimeRecovery(
    previewRecoveryKey,
    previewLoaded,
    async () => {
      const requestedKey = previewRecoveryKeyRef.current;
      const nextTicket = await refreshTicket();
      if (requestedKey && requestedKey === previewRecoveryKeyRef.current && nextTicket && previewDescriptor?.canEmbed &&
        nextTicket.proxyUrl !== previewDescriptor.url) {
        reloadPreview({
          ...previewDescriptor,
          url: nextTicket.proxyUrl,
          embedUrl: nextTicket.proxyUrl,
          proxyExpiresAt: nextTicket.expiresAt,
        });
      }
    },
  );
  const showPreviewFailure = previewUnavailable || previewRecovery.attempts >= 5 && !previewLoaded;
  const showPreviewConnecting = !!previewDescriptor && !previewFrameVisible && !showPreviewFailure;

  useLayoutEffect(() => {
    if (!previewDescriptor || !previewTarget) return undefined;
    return activatePreview(previewDescriptor, previewTarget);
  }, [activatePreview, previewDescriptor, previewTarget]);

  useEffect(() => {
    prewarmPreview(previewDescriptor);
  }, [prewarmPreview, previewDescriptor]);

  useEffect(() => {
    clearPreviewTimers();
    if (previewCacheKey) showPreviewWarmup(null);

    return () => clearPreviewTimers();
  }, [clearPreviewTimers, previewCacheKey, previewDescriptor?.url, showPreviewWarmup]);

  useEffect(() => {
    if (!previewLoaded) {
      if (previewRevealTimerRef.current !== null) {
        window.clearTimeout(previewRevealTimerRef.current);
        previewRevealTimerRef.current = null;
      }
      if (previewCacheKey) showPreviewWarmup(null);
      return;
    }

    if (previewRevealTimerRef.current !== null) {
      window.clearTimeout(previewRevealTimerRef.current);
    }
    previewRevealTimerRef.current = window.setTimeout(() => {
      previewRevealTimerRef.current = null;
      setPreviewWarming(false);
    }, previewWarmupMs === PREVIEW_CACHED_WARMUP_MS ? PREVIEW_CACHED_WARMUP_MS : PREVIEW_READY_REVEAL_DELAY_MS);
  }, [previewCacheKey, previewLoaded, previewWarmupMs, showPreviewWarmup]);

  const progress = active ? job?.progress_percent ?? 5 : app?.status === "ready" ? 100 : 0;
  const quotaPaused = job?.current_step === "waiting_for_provider_quota" && job.status === "waiting_external_api";
  const authBlocked = app?.status === "auth_blocked" || app?.status === "update_auth_blocked";
  const baseStatusDescriptor = app ? getAppStatusDescriptor({ app, job, lifecycle: runtimeLifecycle, runtimeCache }) : null;
  const statusDescriptor = baseStatusDescriptor
    ? getTemplateInstanceStatusDescriptor(baseStatusDescriptor, appTemplateProperties(app?.properties).template_instance?.status)
    : null;
  const storedFeedback = getOperatingSystemFeedbackLabel(app?.latest_job_feedback);
  const failureFeedback = getOperatingSystemFeedbackLabel(job?.error_message || app?.failure_reason);
  const stepFeedback = active
    ? historicalUpdateChecking ? "Checking the previous update" : getOperatingSystemStepLabel(job?.current_step)
    : null;
  const lifecycleFeedback = getOperatingSystemRuntimeLifecycleFeedback(runtimeLifecycle);
  const rawFeedback = authBlocked
    ? storedFeedback || "Paused until an admin reconnects the builder."
    : failureFeedback || storedFeedback || stepFeedback || lifecycleFeedback;
  const feedback = historicalUpdateReview
    ? "Your App is available. The previous update could not be verified, so new changes are paused."
    : historicalUpdateChecking
      ? "Your App is available. We’re checking the previous update."
    : heldLatestUpdate
    ? job?.job_type === "update"
      ? "The latest update could not be verified. You can continue using the available App version."
      : "This App could not be verified yet."
    : statusDescriptor ? getAppStatusFeedback(rawFeedback, statusDescriptor) : rawFeedback;
  const templateProperties = appTemplateProperties(app?.properties);
  const appDescription = getOperatingSystemDescription(app);
  const templateStatus = templateProperties.template?.status;
  const instanceStatus = templateProperties.template_instance?.status;
  const templateSourceArtifactUnavailable = templateProperties.template_instance?.source_artifact_status === "unavailable";
  const canEditTemplateMetadata = templateStatus === "template" && !isTemplateSnapshot && app?.user_id === user?.id;
  const canEditAppMetadata = canManageApp || canEditTemplateMetadata;
  const canConvertToTemplate = !!app && isAppTemplateConversionEligible(
    app,
    isPlatformAdmin,
    !templateSourceArtifactUnavailable && Boolean(app.source_message_id),
    user?.id,
  );
  const templateInstanceFailed = instanceStatus === "failed";
  const supportManagedRecovery = heldLatestUpdate;
  const canRetry = !supportManagedRecovery && !historicalUpdateChecking && !templateSourceArtifactUnavailable && canManageApp && (
    templateInstanceFailed ||
    app?.status === "failed" ||
    app?.status === "update_failed"
  );
  const fixInProgress = active && metaRecord(job?.input_json).fix_request === true;
  const canFix = !supportManagedRecovery && !historicalUpdateChecking && canManageApp && !templateInstanceFailed && !templateSourceArtifactUnavailable &&
    (app?.status === "failed" || app?.status === "update_failed" || job?.status === "failed" || fixInProgress);
  const canManualRestart = !supportManagedRecovery && !historicalUpdateChecking && !templateSourceArtifactUnavailable && canManageApp && (
    templateInstanceFailed ||
    (!!app?.source_message_id && (canRetry || active))
  );
  const statusLabel = templateStatus === "preparing_template"
    ? "preparing_template"
    : templateStatus === "template"
      ? app?.public_access_level === "none" ? "Private Template" : "Published Template"
      : templateStatus === "retiring_template"
        ? "Reverting Template"
        : templateStatus === "retire_failed"
          ? "Revert failed"
          : app
                ? statusDescriptor?.label ?? ""
                : "";
  const retryLabel = templateInstanceFailed ? "Retry" : app ? getOperatingSystemRestartLabel(app) : "Restart build";
  const restartOperation = app ? getOperatingSystemRestartOperation(app) : "create";
  const restartDescription = restartOperation === "update"
    ? `This will start a new update job for this App from its saved source document.${active ? " The current update job will be cancelled." : ""} Progress will start over.`
    : `This will start a new build job for this App from its saved source document.${active ? " The current build job will be cancelled." : ""} Progress will start over, and the current workspace connection will be cleared while the App rebuilds.`;
  // Let the next submission repair the short worker/App-pointer race instead
  // of trapping the owner behind a disabled composer with no active update.
  const updateReady = app?.status === "ready" || app?.status === "update_failed" || staleSupersededUpdate;
  const changeUnavailableReason = !app
    ? null
    : !canManageApp
      ? "Only the owner can edit this App."
      : supportManagedRecovery
      ? historicalUpdateChecking
        ? "Your App is available. We’re checking the previous update."
        : historicalUpdateReview
          ? "Your App is available. The previous update could not be verified, so new changes are paused."
          : "This update could not be verified, so another message cannot be sent yet."
      : active
      ? historicalUpdateChecking
        ? "Your App is available. We’re checking the previous update."
        : "An App update is already running."
      : authBlocked
        ? "An administrator needs to reconnect the builder before this App can be changed."
        : !app.source_message_id
          ? templateSourceArtifactUnavailable
            ? "This Template’s editable source document is unavailable. Use another Template or contact its owner."
            : "This App’s editable source document is missing, so Applyt cannot safely edit it."
            : !hasOperatingSystemEditableRuntime(app) && !staleSupersededUpdate
              ? "This App does not have a connected runtime yet. Rebuild it from the source artifact first."
            : !updateReady
              ? "Wait until this App is ready before applying a change."
              : null;
  const canApplyChange = !!app && canManageApp && !changeUnavailableReason;
  const latestWaitingProviderClarification = latestWaitingProviderClarificationEvent(
    historyEvents,
    app?.latest_job_id,
  );
  const postCompletionClarificationWaiting = isPostCompletionProviderClarification(
    latestWaitingProviderClarification,
    job,
  );
  // A Provider can send one last plain question immediately after it reports
  // the preceding update complete. That question cannot wake the completed
  // job, but the reply is still a valid request: start a fresh update and
  // preserve the direct builder conversation in the activity panel.
  const postCompletionProviderMessageWaiting = !!job &&
    latestProviderMessageRequestsReply(historyEvents, app?.latest_job_id) &&
    isPostCompletionProviderClarification(
      [...historyEvents]
        .filter((event) =>
          event.job_id === app?.latest_job_id &&
          event.source === "base44_editor" &&
          event.event_type === "editor_message" &&
          event.role?.toLowerCase() === "assistant"
        )
        .sort((left, right) => {
          const observedDifference = new Date(right.observed_at).getTime() - new Date(left.observed_at).getTime();
          if (observedDifference) return observedDifference;
          return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
        })[0],
      job,
    );
  const canAnswerProviderClarification = !!app && canManageApp && !authBlocked && !historicalUpdateChecking && (
    active || postCompletionClarificationWaiting || postCompletionProviderMessageWaiting || !!latestWaitingProviderDialog
  );
  const providerInputWaiting = canAnswerProviderClarification && (
    !!latestWaitingProviderClarification ||
    !!latestWaitingProviderDialog ||
    latestProviderMessageRequestsReply(historyEvents, app?.latest_job_id)
  );
  const staleInputFeedback = feedback === "Your input is required to continue this App update." &&
    !providerInputWaiting;
  const headerDetail = feedback && !staleInputFeedback
      && feedback !== statusDescriptor?.availabilityMessage
      && feedback !== statusLabel
      && feedback !== "App ready"
      && feedback !== "Provider preview ready"
      && feedback !== "Template ready"
      ? feedback
      : null;
  const openRunPage = useCallback(() => {
    if (!app?.id) return;
    if (openOperatingSystemRunInNewTab(app.id)) return;

    toast({
      title: "Unable to open new tab",
      description: "Allow pop-ups for Applyt and try again.",
      variant: "destructive",
    });
  }, [app?.id, toast]);
  const previewNotice = authBlocked
    ? "The current App is still available. Updates are paused until an administrator reconnects the builder."
    : previewLoaded
      ? null
      : "Opening the live preview can take a few seconds.";
  const retryJob = async () => {
    if (!app || retrying || !canManageApp || supportManagedRecovery) return;
    if (canFix) {
      setRetrying(true);
      try {
        await invokeAppFunction("start-os-job", { operation: "fix", operatingSystemId: app.id });
        toast({ title: "Fixing App", description: "The App fix has started." });
        await loadOperatingSystem();
      } catch (error) {
        const description = await getSupabaseFunctionErrorMessage(error, "Unable to fix this App.");
        await loadOperatingSystem();
        toast({ title: "Fix failed", description, variant: "destructive" });
      } finally {
        setRetrying(false);
      }
      return;
    }
    if (templateInstanceFailed) {
      setRetrying(true);
      try {
        await retryTemplateApp(app.id);
        toast({ title: "Preparing App", description: "The App retry has started." });
        await loadOperatingSystem();
      } catch (error) {
        toast({
          title: "Retry failed",
          description: error instanceof Error ? error.message : "Unable to retry this App.",
          variant: "destructive",
        });
      } finally {
        setRetrying(false);
      }
      return;
    }
    if (!app.source_message_id) {
      toast({
        title: "Source artifact was deleted",
        description: "This App can still be opened, but it cannot be retried without its source document.",
        variant: "destructive",
      });
      return;
    }
    setRetrying(true);
    try {
      const operation = getOperatingSystemRestartOperation(app);
      const data = await invokeAppFunction<Record<string, unknown>>("start-os-job", {
        operation,
        operatingSystemId: app.id,
        artifactMessageId: app.source_message_id,
        artifactName: app.name,
        retry: true,
        forceRestart: active,
      });
      if (operation === "update" && !data?.reused && typeof data?.job_id === "string") rememberAppUpdateNotificationJob(data.job_id);
      toast({ title: operation === "update" ? "App update restarted" : "App build restarted" });
      await loadOperatingSystem();
    } catch (error) {
      const description = await getSupabaseFunctionErrorMessage(error, "Unable to retry this job.");
      toast({
        title: "Retry failed",
        description,
        variant: "destructive",
      });
    } finally {
      setRetrying(false);
    }
  };

  const requestRestart = () => {
    if (!app || retrying || restartSubmittingRef.current || !canManageApp || supportManagedRecovery || historicalUpdateChecking) return;
    if (canFix || templateInstanceFailed) {
      void retryJob();
      return;
    }
    restartTargetIdRef.current = app.id;
    setRestartConfirmOpen(true);
  };

  const confirmRestart = async () => {
    if (!restartConfirmOpen || retrying || restartSubmittingRef.current) return;
    if (!app || app.id !== restartTargetIdRef.current || canFix || templateInstanceFailed || supportManagedRecovery || historicalUpdateChecking) {
      setRestartConfirmOpen(false);
      return;
    }
    restartSubmittingRef.current = true;
    try {
      await retryJob();
    } finally {
      setRestartConfirmOpen(false);
      restartSubmittingRef.current = false;
    }
  };

  const runQuotaAction = async (action: "check_again" | "cancel_update") => {
    if (!app || !job || !canManageApp || quotaAction) return;
    setQuotaAction(action);
    try {
      const result = await invokeAppFunction<{ available?: boolean; cancelled?: boolean }>("manage-os-quota-pause", {
        action,
        operatingSystemId: app.id,
        jobId: job.id,
      });
      if (action === "check_again") {
        toast({
          title: result.available ? "Quota is available" : "Quota is still unavailable",
          description: result.available
            ? "The update will resume automatically after the worker confirms recovery."
            : "We’ll keep checking automatically. Your current App version is still available.",
        });
      } else {
        toast({ title: "Update cancelled", description: "Your current App version is unchanged." });
        await loadOperatingSystem();
      }
    } catch (error) {
      toast({
        title: action === "check_again" ? "Could not check quota" : "Could not cancel update",
        description: await getSupabaseFunctionErrorMessage(error, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setQuotaAction(null);
    }
  };

  const recoverStuckUpdate = async (action: "stop" | "check_now") => {
    if (!app || !job || !stuckUpdate || stuckUpdateAction) return;
    setStuckUpdateAction(action);
    try {
      await invokeAppFunction("recover-stuck-os-update", {
        action, operatingSystemId: app.id, jobId: job.id,
      });
      toast(action === "stop"
        ? { title: "Update stopped", description: "Your working App version is available again." }
        : { title: "Update check requested", description: "We’ll verify this update using its existing job." });
      setStopUpdateConfirmOpen(false);
      await loadOperatingSystem();
    } catch (error) {
      toast({
        title: action === "stop" ? "Could not safely stop the update" : "Could not check the update",
        description: await getSupabaseFunctionErrorMessage(error, "Please try again later."),
        variant: "destructive",
      });
    } finally {
      setStuckUpdateAction(null);
    }
  };

  const deleteOperatingSystem = async () => {
    if (!app || deleting || !canManageApp) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        deleted?: number;
        provider_deleted?: number;
        provider_already_deleted?: number;
        provider_failed?: number;
        error?: string;
      }>("delete-operating-systems", {
        body: { operatingSystemId: app.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast({
        title: "App deleted",
        description: `"${app.name}" was removed from Applyt.`,
      });
      setDeleteConfirmOpen(false);
      navigate("/applications");
    } catch (error) {
      const description = await getSupabaseFunctionErrorMessage(
        error,
        "Unable to delete this App.",
      );
      toast({
        title: "Delete failed",
        description,
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const convertToTemplate = async () => {
    if (!app || !canConvertToTemplate || templateConverting) return;
    setTemplateConverting(true);
    try {
      const result = await convertAppToTemplate(app.id);
      if (result.status === "failed") {
        throw new Error("Template preparation failed. Your original App remains available.");
      }
      toast({
        title: "Preparing Template",
        description: "A private Template snapshot is being prepared. Your App stays available.",
        action: <ToastAction altText="View Template" onClick={() => navigate(`/applications/${result.app_id}`)}>View Template</ToastAction>,
      });
      setSourceExportStatus({ app_id: app.id, status: "queued", job_id: result.job_id, source_unmanaged: false });
      setTemplateConversionOpen(false);
      await loadOperatingSystem();
    } catch (error) {
      const copyServiceSetupRequired = error instanceof AppTemplateActionError && [
        "copy_service_not_configured",
        "copy_service_auth_failed",
        "copy_service_configuration_invalid",
        "copy_service_unavailable",
      ].includes(error.code);
      const templatePreflightBlocked = copyServiceSetupRequired
        || (error instanceof AppTemplateActionError && ["template_preview_not_configured", "template_source_unmanaged"].includes(error.code));
      if (templatePreflightBlocked) setTemplateConversionOpen(false);
      toast({
        title: templatePreflightBlocked ? "Template not started" : "Template preparation failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
        action: copyServiceSetupRequired ? (
          <ToastAction
            altText="Open CopyService settings"
            onClick={() => navigate("/admin?tab=CopyService")}
          >
            Open settings
          </ToastAction>
        ) : undefined,
      });
    } finally {
      setTemplateConverting(false);
    }
  };

  const downloadDocumentation = async () => {
    if (!app || documentationDownloading) return;
    if (!app.source_message_id || !documentPdf?.pdfUrl) {
      toast({
        title: "Documentation PDF unavailable",
        description: "Applyt is still generating the PDF for this App.",
        variant: "destructive",
      });
      return;
    }

    setDocumentationDownloading(true);
    try {
      const link = document.createElement("a");
      link.href = documentPdf.pdfUrl;
      link.download = buildDocumentationDownloadFilename(app, documentPdf.title);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Document downloaded",
        description: "The PDF file has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Download failed",
        description: error instanceof Error ? error.message : "Unable to download this documentation PDF.",
        variant: "destructive",
      });
    } finally {
      setDocumentationDownloading(false);
    }
  };

  const updateChangeRequest = useCallback((value: string) => {
    changeRequestRef.current = value;
    setChangeRequest(value);
    if (app?.id) writeAppComposerDraft(app.id, value);
    setChangeError(null);
  }, [app?.id]);

  useEffect(() => {
    if (!app?.id || changeRequest || changeSubmitting) return;
    const draft = readAppComposerDraft(app.id);
    if (draft) setChangeRequest(draft);
  }, [app?.id, changeRequest, changeSubmitting]);

  const queueChangeRequest = useCallback(async (
    request: string,
    reference: RouterRequestReference = {},
    appAction?: AppActionMetadata,
    allowActiveRouterClarification = false,
  ): Promise<boolean> => {
    if (!app || (!independentMessaging && changeSubmitting)) return false;
    if (independentMessaging && !appAction && !reference.replyToDecisionId) {
      if (!canManageApp || !request.trim()) return false;
      if (changeAttachments.length) { setChangeError("Remove the attachments before sending this text message."); return false; }
      const key = JSON.stringify({ applicationId: app.id, text: request });
      const requestId = pendingAppMessageRequestIdsRef.current.get(key) ?? createUuid();
      pendingAppMessageRequestIdsRef.current.set(key, requestId);
      pendingRouterRequestRef.current = { key, requestId };
      setChangeSubmitting(true);
      setChangeError(null);
      const restoreRequestIfComposerIsEmpty = () => {
        if (changeRequestRef.current) return;
        changeRequestRef.current = request;
        setChangeRequest(request);
        writeAppComposerDraft(app.id, request);
      };
      if (changeRequestRef.current === request) {
        changeRequestRef.current = "";
        setChangeRequest("");
        clearAppComposerDraft(app.id);
      }
      try {
        const receipt = await sendIndependentAppMessage(app.id, requestId, request, invokeAppFunction);
        const isCurrentRequest = pendingRouterRequestRef.current?.requestId === requestId;
        if (receipt.deliveryState === "rejected") {
          pendingAppMessageRequestIdsRef.current.delete(key);
          if (isCurrentRequest) pendingRouterRequestRef.current = null;
          restoreRequestIfComposerIsEmpty();
          if (isCurrentRequest) {
            setChangeError(receipt.message || "The message was not accepted. You can try again.");
          }
          return false;
        }
        if (receipt.deliveryState !== "accepted") {
          // Preserve this ID for an uncertain send; the next distinct request remains independent.
          restoreRequestIfComposerIsEmpty();
          if (isCurrentRequest) {
            setChangeError(receipt.message || "Delivery could not be confirmed. You can continue working.");
          }
          return false;
        }
        if (isCurrentRequest) pendingRouterRequestRef.current = null;
        pendingAppMessageRequestIdsRef.current.delete(key);
        setChangeAttachments([]);
        return true;
      } catch {
        const isCurrentRequest = pendingRouterRequestRef.current?.requestId === requestId;
        restoreRequestIfComposerIsEmpty();
        if (isCurrentRequest) {
          setChangeError("Delivery could not be confirmed. Your draft has been kept; you can continue working.");
        }
        return false;
      } finally {
        setChangeSubmitting(false);
        void loadHistory(app.id);
      }
    }
    if (historicalUpdateChecking) return false;
    if (appAction?.type.startsWith("agentic_flow_") && !isAgenticUiAvailableHere()) return false;

    const trimmedRequest = request; // Validation must not rewrite the submitted message.
    const directMessage = !appAction && !reference.replyToDecisionId;
    if (directMessage && changeAttachments.length > 0) {
      setChangeError("This conversation supports text messages only. Remove the attachments before sending.");
      return false;
    }
    if (!trimmedRequest.trim() && changeAttachments.length === 0) {
      setChangeError("Ask a question, describe a change, or attach a file before sending it.");
      return false;
    }
    const canSubmitRequest = canApplyChange || (
      allowActiveRouterClarification &&
      canManageApp &&
      !!reference.replyToDecisionId
    );
    if (!canSubmitRequest || !app.source_message_id) {
      setChangeError(changeUnavailableReason || "This App cannot be changed right now.");
      return false;
    }

    const requestKey = JSON.stringify({
      request: trimmedRequest,
      replyToDecisionId: reference.replyToDecisionId ?? null,
      appAction: appAction ?? null,
    });
    const pendingRequest = pendingRouterRequestRef.current;
    const requestId = pendingRequest?.key === requestKey
      ? pendingRequest.requestId
      : createUuid();
    pendingRouterRequestRef.current = { key: requestKey, requestId };

    const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
    if (changeAttachments.length > 5 || changeAttachments.some((file) => file.size <= 0 || file.size > MAX_ATTACHMENT_BYTES)) {
      setChangeError("Attach up to 5 files, each no larger than 10 MB.");
      pendingRouterRequestRef.current = null;
      return false;
    }
    if (changeAttachments.some((file) => !recognizeAttachment(file.name, file.type))) {
      setChangeError("Unsupported attachment type. Choose a supported document or image.");
      pendingRouterRequestRef.current = null;
      return false;
    }

    const submittedAt = new Date().toISOString();
    const optimisticAttachments = optimisticAttachmentMetadata(changeAttachments);
    setOptimisticChangeRequest({
      id: `optimistic-${requestId}`,
      operating_system_id: app.id,
      job_id: app.latest_job_id,
      user_id: app.user_id,
      source: "applyt",
      event_type: "change_request",
      role: "user",
      content: trimmedRequest || attachmentOnlyRequestContent(optimisticAttachments.length),
      public_metadata: {
        submission_state: "submitting",
        ...(optimisticAttachments.length ? { attachments: optimisticAttachments } : {}),
      },
      observed_at: submittedAt,
      created_at: submittedAt,
    });
    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const attachments = await Promise.all(changeAttachments.map(async (file) => {
        const safeName = file.name.replace(/[^A-Za-z0-9._-]/g, "_").slice(-120) || "attachment";
        const path = `${user!.id}/${app.id}/${requestId}/${createUuid()}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from("operating-system-attachments")
          .upload(path, file, { contentType: file.type || "application/octet-stream", upsert: false });
        if (uploadError) throw new Error("Unable to upload an attachment. Please try again.");
        return { path, name: file.name, contentType: file.type || "application/octet-stream", size: file.size };
      }));
      const data = await invokeAppFunction<{
        outcome?: string; message?: string; job_id?: string; reused?: boolean;
        discovery_id?: string; candidates?: unknown[];
      }>("start-os-job", {
          operation: "update",
          operatingSystemId: app.id,
          artifactMessageId: app.source_message_id,
          artifactName: app.name,
          ...(trimmedRequest ? { changeRequest: trimmedRequest } : {}),
          ...(reference.replyToDecisionId ? { replyToDecisionId: reference.replyToDecisionId } : {}),
          ...(attachments.length ? { attachments } : {}),
          ...(appAction ? { appAction } : {}),
          requestId,
        
      });

      const requestOutcome = appRequestOutcome(data);
      if (requestOutcome === "completed" || requestOutcome === "suggestions") {
        pendingRouterRequestRef.current = null;
        toast({
          title: requestOutcome === "suggestions" && isAgenticUiAvailableHere() ? "Execution ideas found" : "Applyt",
          description: requestOutcome === "suggestions" && !isAgenticUiAvailableHere()
            ? "Your request was processed."
            : data?.message ?? "Your request was processed.",
        });
        setChangeRequest("");
        clearAppComposerDraft(app.id);
        setChangeAttachments([]);
        await loadHistory(app.id);
        await loadOperatingSystem();
        setOptimisticChangeRequest(null);
        return true;
      }

      if (requestOutcome !== "job_started") {
        throw new Error("The App request returned an unexpected response. Please retry.");
      }

      if (!data?.reused) rememberAppUpdateNotificationJob(data?.job_id);

      pendingRouterRequestRef.current = null;
      toast({
        title: data?.reused ? "Message already submitted" : "Message submitted",
        description: "Sending to the Provider.",
      });
      setChangeRequest("");
      clearAppComposerDraft(app.id);
      setChangeAttachments([]);
      await loadHistory(app.id);
      await loadOperatingSystem();
      setOptimisticChangeRequest(null);
      return true;
    } catch (error) {
      const { code, message: description } = await getSupabaseFunctionErrorDetails(
        error,
        "Unable to start this App change.",
      );
      if (code === "historical_update_verification_in_progress") {
        await loadOperatingSystem();
      }
      setChangeError(description);
      setOptimisticChangeRequest(null);
      // Keep an idempotency key only while the server may still be processing
      // it or intentionally asked for a same-request retry. A terminal
      // validation/action failure needs a fresh user submission instead.
      if (!directMessage && !["router_unavailable", "request_in_progress"].includes(code)) {
        pendingRouterRequestRef.current = null;
      }
      toast({
        title: "Change could not start",
        description,
        variant: "destructive",
      });
      return false;
    } finally {
      setChangeSubmitting(false);
    }
  }, [
    app,
    changeAttachments,
    canApplyChange,
    canManageApp,
    historicalUpdateChecking,
    independentMessaging,
    changeSubmitting,
    changeUnavailableReason,
    invokeAppFunction,
    loadHistory,
    loadOperatingSystem,
    toast,
    user,
  ]);

  const submitChangeRequest = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void queueChangeRequest(changeRequest, {}, isAgenticUiAvailableHere() && agenticFlowTarget ? {
      type: "agentic_flow_modify",
      flowId: agenticFlowTarget.id,
      displayName: agenticFlowTarget.name,
    } : undefined).then((accepted) => {
      if (accepted) setAgenticFlowTarget(null);
    });
  }, [agenticFlowTarget, changeRequest, queueChangeRequest]);

  const loadAgenticFlowList = useCallback((includeArchived = false): Promise<AgenticFlowListResponse> => {
    if (!isAgenticUiAvailableHere()) return Promise.reject(new Error("Execution plans are unavailable here."));
    return invokeAppFunction<AgenticFlowListResponse>("manage-agentic-flows", {
      action: "list",
      operatingSystemId: app?.id,
      includeArchived,
    });
  }, [app?.id, invokeAppFunction]);

  const loadAgenticFlowDetail = useCallback(async (flowId: string): Promise<AgenticFlowProductDetail> => {
    if (!isAgenticUiAvailableHere()) throw new Error("Execution plans are unavailable here.");
    const result = await invokeAppFunction<{ flow: AgenticFlowProductDetail }>("manage-agentic-flows", {
      action: "detail", operatingSystemId: app?.id, flowId,
    });
    return result.flow;
  }, [app?.id, invokeAppFunction]);

  const loadAgenticFlowDebug = useCallback(async (flowId: string): Promise<AgenticFlowDebugDetail> => {
    if (!isAgenticUiAvailableHere()) throw new Error("Execution plans are unavailable here.");
    const result = await invokeAppFunction<{ debug: AgenticFlowDebugDetail }>("manage-agentic-flows", {
      action: "debug", operatingSystemId: app?.id, flowId,
    });
    return result.debug;
  }, [app?.id, invokeAppFunction]);

  const runAgenticFlow = useCallback(async (flowId: string) => {
    if (!isAgenticUiAvailableHere()) return;
    const result = await invokeAppFunction<{ ok?: boolean; message?: string }>("manage-agentic-flows", {
      action: "run", operatingSystemId: app?.id, flowId,
    });
    if (!result?.ok) throw new Error(result?.message ?? "The review could not be completed.");
    toast({
      title: "Run finished",
      description: "Applyt completed this run request.",
    });
    if (app) await Promise.all([loadHistory(app.id), loadOperatingSystem()]);
  }, [app, invokeAppFunction, loadHistory, loadOperatingSystem, toast]);

  const recurrenceAction = useCallback(async (flowId: string, action: "test" | "enable_recurrence" | "recover_recurrence", actionVersion: string) => {
    if (!isAgenticUiAvailableHere()) return;
    const result = await invokeAppFunction<{ ok?: boolean; message?: string }>("manage-agentic-flows", {
      action, operatingSystemId: app?.id, flowId, actionVersion,
      ...(action === "test" ? { requestId: crypto.randomUUID() } : {}),
    });
    if (!result?.ok) throw new Error(result?.message ?? "This action could not be completed safely.");
    if (app) await Promise.all([loadHistory(app.id), loadOperatingSystem()]);
  }, [app, invokeAppFunction, loadHistory, loadOperatingSystem]);

  const checkAgenticSetup = useCallback(async (flowId: string, actionVersion: string) => {
    if (!isAgenticUiAvailableHere()) return;
    const result = await invokeAppFunction<{ ok?: boolean; message?: string }>("manage-agentic-flows", {
      action: "recheck_setup", operatingSystemId: app?.id, flowId, actionVersion,
    });
    if (!result?.ok) throw new Error(result?.message ?? "The setup could not be checked safely.");
    if (app) await Promise.all([loadHistory(app.id), loadOperatingSystem()]);
  }, [app, invokeAppFunction, loadHistory, loadOperatingSystem]);

  const mutateAgenticFlow = useCallback(async (flowId: string, action: "pause" | "resume" | "archive") => {
    if (!isAgenticUiAvailableHere()) return;
    const result = await invokeAppFunction<{ ok?: boolean; message?: string }>("manage-agentic-flows", {
      action, operatingSystemId: app?.id, flowId,
    });
    if (!result?.ok) throw new Error(result?.message ?? "That execution plan action could not be completed.");
    toast({
      title: action === "pause" ? "Execution plan paused" : action === "resume" ? "Execution plan active" : "Execution plan archived",
      description: action === "pause" ? "Future execution is blocked." : action === "resume" ? "The plan is available again. Check its schedule status before relying on recurring runs." : "Its activity history has been kept.",
    });
    if (app) await Promise.all([loadHistory(app.id), loadOperatingSystem()]);
  }, [app, invokeAppFunction, loadHistory, loadOperatingSystem, toast]);

  const retryInterruptedAuthorization = useCallback(async (serviceName: string): Promise<boolean> => {
    const safeServiceName = serviceName
      .replace(/[^A-Za-z0-9 .&()+-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 80) || "account";
    return queueChangeRequest(
      `Continue setting up the ${safeServiceName} connection for this App. Start a fresh connection request and wait for the new authorization card before connecting.`,
    );
  }, [queueChangeRequest]);

  const panelHistoryEvents = useMemo(() => {
    if (!optimisticChangeRequest) return historyEvents;
    const optimisticTime = Date.parse(optimisticChangeRequest.observed_at);
    const hasPersistedRequest = historyEvents.some((event) => {
      const eventTime = Date.parse(event.observed_at);
      return event.source === "applyt" &&
        event.event_type === "change_request" &&
        event.content?.trim() === optimisticChangeRequest.content?.trim() &&
        Number.isFinite(eventTime) && Number.isFinite(optimisticTime) &&
        eventTime >= optimisticTime - 2_000;
    });
    return hasPersistedRequest ? historyEvents : [...historyEvents, optimisticChangeRequest];
  }, [historyEvents, optimisticChangeRequest]);

  const actionableActiveRouterClarificationDecisionId = useMemo(() => {
    if (!isAgenticUiAvailableHere()) return null;
    const decisionId = currentAgenticFlowActivity(panelHistoryEvents).actionableClarificationDecisionId;
    const clarificationEvent = decisionId
      ? panelHistoryEvents.find((event) =>
        event.source === "applyt" &&
        event.event_type === "router_clarification" &&
        (event.public_metadata as Record<string, unknown> | null)?.router_decision_id === decisionId,
      )
      : null;
    if (!decisionId || !clarificationEvent || routerClarificationAssociationState({
      event: clarificationEvent,
      events: panelHistoryEvents,
      latestJobId: app?.latest_job_id,
      job,
    }) !== "actionable") {
      return null;
    }
    return decisionId;
  }, [app?.latest_job_id, job, panelHistoryEvents]);
  const canAnswerActiveRouterClarification =
    canManageApp &&
    !!actionableActiveRouterClarificationDecisionId;

  const submitClarifyingAnswer = useCallback(async (
    clarificationEventId: string,
    answers: ProviderClarificationAnswer[],
  ): Promise<boolean> => {
    if (!app || changeSubmitting) return false;
    if (!canAnswerProviderClarification) {
      setChangeError("This build question is no longer waiting for an answer.");
      return false;
    }

    const requestKey = JSON.stringify({ clarificationEventId, answers });
    const pendingRequest = pendingProviderClarificationRef.current;
    const requestId = pendingRequest?.key === requestKey
      ? pendingRequest.requestId
      : createUuid();
    pendingProviderClarificationRef.current = { key: requestKey, requestId };

    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const data = await invokeAppFunction<{ submitted?: boolean }>("submit-os-clarification", {
          operatingSystemId: app.id,
          clarificationEventId,
          answers,
          requestId,
        
      });
      if (data?.submitted !== true) {
        throw new Error("The answer returned an unexpected response. Please retry.");
      }

      pendingProviderClarificationRef.current = null;
      toast({
        title: "Answer sent",
        description: "The App update is continuing.",
      });
      await Promise.all([
        loadHistory(app.id),
        loadOperatingSystem(),
      ]);
      void syncHistory();
      return true;
    } catch (error) {
      const { code, message: description } = await getSupabaseFunctionErrorDetails(
        error,
        "Unable to send this answer.",
      );
      setChangeError(description);
      if (code !== "clarification_answer_in_progress") {
        pendingProviderClarificationRef.current = null;
      }
      toast({
        title: "Answer could not be sent",
        description,
        variant: "destructive",
      });
      return false;
    } finally {
      setChangeSubmitting(false);
    }
  }, [
    app,
    canAnswerProviderClarification,
    changeSubmitting,
    invokeAppFunction,
    loadHistory,
    loadOperatingSystem,
    syncHistory,
    toast,
  ]);

  const loadProviderDialog = useCallback(async (
    providerDialogEventId: string,
  ): Promise<ProviderDialogDescription | null> => {
    if (!app || !canAnswerProviderClarification) return null;
    try {
      const data = await invokeAppFunction<Record<string, unknown>>("submit-os-provider-dialog", {
          operatingSystemId: app.id,
          providerDialogEventId,
          action: "describe",
          requestId: createUuid(),
        
      });
      return parseProviderDialogDescription(data?.dialog);
    } catch (error) {
      const { message: description } = await getSupabaseFunctionErrorDetails(
        error,
        "Unable to load the requested fields.",
      );
      setChangeError(description);
      return null;
    }
  }, [app, canAnswerProviderClarification, invokeAppFunction]);

  const submitProviderDialogAction = useCallback(async (
    providerDialogEventId: string,
    action: ProviderDialogAction,
    form?: ProviderDialogFormValues,
  ): Promise<boolean> => {
    if (!app || changeSubmitting) return false;
    if (!canAnswerProviderClarification) {
      setChangeError("This provider request is no longer waiting for a response.");
      return false;
    }

    let authorizationWindow: Window | null = null;
    if (action === "begin_oauth") {
      authorizationWindow = window.open(
        "about:blank",
        "_blank",
        "popup=yes,width=560,height=760,resizable=yes,scrollbars=yes",
      );
      if (!authorizationWindow) {
        const description = "Allow pop-ups for this site, then try connecting again.";
        setChangeError(description);
        toast({ title: "Connection window blocked", description, variant: "destructive" });
        return false;
      }
      authorizationWindowRef.current = authorizationWindow;
      authorizationDialogEventIdRef.current = providerDialogEventId;
      const authorizationDialog = historyEvents.find((event) => event.id === providerDialogEventId);
      const authorizationDialogKey = metaRecord(authorizationDialog?.public_metadata).dialog_key;
      authorizationDialogKeyRef.current = typeof authorizationDialogKey === "string"
        ? authorizationDialogKey
        : null;
      authorizationJobIdRef.current = authorizationDialog?.job_id ?? app.latest_job_id ?? null;
    }

    const requestKey = JSON.stringify({ providerDialogEventId, action });
    const pendingRequest = pendingProviderDialogRef.current;
    const requestId = pendingRequest?.key === requestKey
      ? pendingRequest.requestId
      : createUuid();
    pendingProviderDialogRef.current = { key: requestKey, requestId };

    const invokeDialogAction = async (
      nextAction: ProviderDialogAction,
      nextForm?: ProviderDialogFormValues,
    ) => {
      const data = await invokeAppFunction<Record<string, unknown>>("submit-os-provider-dialog", {
          operatingSystemId: app.id,
          providerDialogEventId,
          action: nextAction,
          requestId,
          ...(nextForm ? { form: nextForm } : {}),
        
      });
      return (data ?? {}) as {
        submitted?: boolean;
        authorization_required?: boolean;
        authorization_pending?: boolean;
        redirect_url?: string;
        poll_after_ms?: number;
      };
    };

    setChangeSubmitting(true);
    setChangeError(null);
    try {
      let result = await invokeDialogAction(action, form);

      if (action === "begin_oauth" && result.authorization_required) {
        const redirectUrl = result.redirect_url;
        let safeRedirectUrl: string | null = null;
        try {
          const parsed = redirectUrl ? new URL(redirectUrl) : null;
          safeRedirectUrl = parsed?.protocol === "https:" ? parsed.toString() : null;
        } catch {
          safeRedirectUrl = null;
        }
        if (!safeRedirectUrl) {
          throw new Error("The secure connection window could not be opened. Please retry.");
        }
        setAuthorizationWindowUrl(safeRedirectUrl);
        authorizationWindow?.location.replace(safeRedirectUrl);

        for (let poll = 0; poll < PROVIDER_AUTHORIZATION_MAX_POLLS; poll += 1) {
          const pollAfterMs = Math.min(5_000, Math.max(1_000, result.poll_after_ms ?? 3_000));
          let authorizationWindowClosed = authorizationWindow?.closed === true;
          for (let remainingMs = pollAfterMs; !authorizationWindowClosed && remainingMs > 0; remainingMs -= 250) {
            const waitMs = Math.min(250, remainingMs);
            await new Promise<void>((resolve) => window.setTimeout(resolve, waitMs));
            authorizationWindowClosed = authorizationWindow?.closed === true;
          }
          if (authorizationWindowClosed) {
            authorizationWindowRef.current = null;
            authorizationDialogEventIdRef.current = null;
            authorizationDialogKeyRef.current = null;
            authorizationJobIdRef.current = null;
            pendingProviderDialogRef.current = null;
            setAuthorizationWindowUrl(null);
            setChangeSubmitting(false);
            toast({
              title: "Connection paused",
              description: "The secure window was closed. Reopen it when you are ready to continue.",
            });
            await Promise.allSettled([
              loadHistory(app.id),
              loadOperatingSystem(),
            ]);
            void syncHistory();
            return false;
          }
          result = await invokeDialogAction("poll_oauth");
          if (result.submitted) break;
          if (!result.authorization_pending) {
            throw new Error("The account connection returned an unexpected response. Please retry.");
          }
        }
        if (!result.submitted) {
          throw new Error("The account connection is still waiting. Use Check connection when it is complete.");
        }
      }

      if (action === "poll_oauth" && result.authorization_pending) {
        pendingProviderDialogRef.current = null;
        toast({
          title: "Connection still pending",
          description: "Finish connecting in the secure window, then check again.",
        });
        return false;
      }
      if (result.submitted !== true) {
        throw new Error("The provider request returned an unexpected response. Please retry.");
      }

      pendingProviderDialogRef.current = null;
      authorizationDialogEventIdRef.current = null;
      authorizationDialogKeyRef.current = null;
      authorizationJobIdRef.current = null;
      setAuthorizationWindowUrl(null);
      closeAuthorizationWindow();
      toast({
        title: action === "reject"
          ? "Request skipped"
          : action === "submit_form"
          ? "Connector configured"
          : action === "begin_oauth" || action === "poll_oauth"
          ? "Account connected"
          : "Request approved",
        description: "The App update is continuing.",
      });
      await Promise.all([
        loadHistory(app.id),
        loadOperatingSystem(),
      ]);
      void syncHistory();
      return true;
    } catch (error) {
      authorizationDialogEventIdRef.current = null;
      authorizationDialogKeyRef.current = null;
      authorizationJobIdRef.current = null;
      setAuthorizationWindowUrl(null);
      closeAuthorizationWindow();
      const { code, message: description } = await getSupabaseFunctionErrorDetails(
        error,
        "Unable to respond to this request.",
      );
      if (isSupabaseFunctionIdleTimeoutMessage(description)) {
        pendingProviderDialogRef.current = null;
        setChangeError(null);
        toast({
          title: "Request still processing",
          description: "Applyt received the response and is continuing the App update in the background.",
        });
        await Promise.allSettled([
          loadHistory(app.id),
          loadOperatingSystem(),
        ]);
        void syncHistory();
        return true;
      }
      setChangeError(description);
      if (code !== "provider_dialog_response_in_progress") {
        pendingProviderDialogRef.current = null;
      }
      toast({
        title: "Request could not be completed",
        description,
        variant: "destructive",
      });
      return false;
    } finally {
      setChangeSubmitting(false);
    }
  }, [
    app,
    canAnswerProviderClarification,
    changeSubmitting,
    historyEvents,
    invokeAppFunction,
    loadHistory,
    loadOperatingSystem,
    closeAuthorizationWindow,
    syncHistory,
    toast,
  ]);

  // V2 cards expose only a short-lived opaque reference.  The server reloads
  // and validates the private provider snapshot before it performs anything.
  const submitProviderUiAction = useCallback(async (
    providerDialogEventId: string,
    actionRef: string,
    input: Record<string, unknown>,
  ): Promise<boolean> => {
    if (!app || changeSubmitting || !canAnswerProviderClarification) return false;
    const requestKey = JSON.stringify({ providerDialogEventId, actionRef, input });
    const pending = pendingProviderDialogRef.current;
    const requestId = pending?.key === requestKey ? pending.requestId : createUuid();
    pendingProviderDialogRef.current = { key: requestKey, requestId };
    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const result = await invokeAppFunction<{ submitted?: boolean }>("submit-os-provider-dialog", {
        operatingSystemId: app.id,
        providerDialogEventId,
        action_ref: actionRef,
        ...(Object.keys(input).length ? { input } : {}),
        requestId,
      });
      if (result?.submitted !== true) throw new Error("The provider request returned an unexpected response. Please retry.");
      pendingProviderDialogRef.current = null;
      toast({ title: "Response sent", description: "The App update is continuing." });
      await Promise.all([loadHistory(app.id), loadOperatingSystem()]);
      void syncHistory();
      return true;
    } catch (error) {
      const { code, message } = await getSupabaseFunctionErrorDetails(error, "Unable to respond to this request.");
      setChangeError(message);
      if (code !== "provider_dialog_response_in_progress") pendingProviderDialogRef.current = null;
      toast({ title: "Request could not be completed", description: message, variant: "destructive" });
      return false;
    } finally {
      setChangeSubmitting(false);
    }
  }, [app, canAnswerProviderClarification, changeSubmitting, invokeAppFunction, loadHistory, loadOperatingSystem, syncHistory, toast]);

  const submitProviderMessageReply = useCallback(async (
    providerMessageEventId: string,
    reply: string,
  ): Promise<boolean> => {
    if (!app || changeSubmitting) return false;

    const trimmedReply = reply;
    if (!trimmedReply.trim()) {
      setChangeError("Write a reply before sending it.");
      return false;
    }

    // Completed jobs are deliberately immutable. Continue the visible
    // conversation as a new update instead of trying to wake the old job;
    // queueChangeRequest creates the durable activity event before the
    // Provider handoff.
    if (job?.status === "completed") {
      return queueChangeRequest(trimmedReply);
    }

    const requestKey = JSON.stringify({ providerMessageEventId, reply: trimmedReply });
    const pendingRequest = pendingProviderMessageRef.current;
    const requestId = pendingRequest?.key === requestKey
      ? pendingRequest.requestId
      : createUuid();
    pendingProviderMessageRef.current = { key: requestKey, requestId };

    setChangeSubmitting(true);
    setChangeError(null);
    try {
      const data = await invokeAppFunction<{ queued?: boolean; submitted?: boolean }>("submit-os-provider-message", {
          operatingSystemId: app.id,
          providerMessageEventId,
          message: trimmedReply,
          requestId,
        
      });
      if (data?.queued !== true && data?.submitted !== true) {
        throw new Error("The reply returned an unexpected response. Please retry.");
      }

      pendingProviderMessageRef.current = null;
      setChangeRequest("");
      clearAppComposerDraft(app.id);
      toast({
        title: "Reply sent",
        description: "The App update is continuing.",
      });
      await Promise.all([
        loadHistory(app.id),
        loadOperatingSystem(),
      ]);
      void syncHistory();
      return true;
    } catch (error) {
      const { code, message: description } = await getSupabaseFunctionErrorDetails(
        error,
        "Unable to send this reply.",
      );
      if (isStaleProviderMessageReplyError(code, description)) {
        pendingProviderMessageRef.current = null;
        setChangeRequest("");
        setChangeError(null);
        await Promise.all([
          loadHistory(app.id),
          loadOperatingSystem(),
        ]);
        void syncHistory();
        toast({
          title: "App activity updated",
          description: "That question was already resolved or replaced. Review the latest activity to continue.",
        });
        return false;
      }
      setChangeError(description);
      if (code !== "provider_message_reply_in_progress") {
        pendingProviderMessageRef.current = null;
      }
      toast({
        title: "Reply could not be sent",
        description,
        variant: "destructive",
      });
      return false;
    } finally {
      setChangeSubmitting(false);
    }
  }, [
    app,
    changeSubmitting,
    invokeAppFunction,
    job?.status,
    loadHistory,
    loadOperatingSystem,
    queueChangeRequest,
    syncHistory,
    toast,
  ]);

  const submitRouterClarifyingAnswer = useCallback((decisionId: string, answer: string) => {
    if (!isAgenticUiAvailableHere() && panelHistoryEvents.some((event) =>
      event.event_type === "router_clarification" &&
      (event.public_metadata as Record<string, unknown> | null)?.router_decision_id === decisionId &&
      isAgenticFlowActivityEvent(event)
    )) return Promise.resolve(false);
    if (!canAnswerActiveRouterClarification || decisionId !== actionableActiveRouterClarificationDecisionId) {
      setChangeError("This setup question is no longer waiting for an answer.");
      return Promise.resolve(false);
    }
    return queueChangeRequest(answer, { replyToDecisionId: decisionId }, undefined, true);
  }, [
    actionableActiveRouterClarificationDecisionId,
    canAnswerActiveRouterClarification,
    panelHistoryEvents,
    queueChangeRequest,
  ]);

  const startRename = () => {
    if (!app) return;
    setRenameValue(app.name);
    setRenaming(true);
  };

  const cancelRename = () => {
    setRenaming(false);
    setRenameValue("");
  };

  const startDescriptionEdit = () => {
    setDescriptionValue(appDescription ?? "");
    setDescriptionEditing(true);
  };

  const cancelDescriptionEdit = () => {
    setDescriptionEditing(false);
    setDescriptionValue("");
  };

  const saveRename = async () => {
    if (!app || !canEditAppMetadata || renameSaving) return;
    const nextName = renameValue.replace(/\s+/g, " ").trim();
    if (!nextName) {
      toast({
        title: "Name is required",
        description: "Add a short name for this App.",
        variant: "destructive",
      });
      return;
    }
    if (nextName === app.name) {
      cancelRename();
      return;
    }

    setRenameSaving(true);
    try {
      const data = await invokeAppFunction<{ name?: string }>("rename-operating-system", {
          operatingSystemId: app.id,
          name: nextName,
        
      });
      const result = (data ?? {}) as { name?: string };
      const savedName = result.name || nextName;
      setApp((prev) => prev ? { ...prev, name: savedName, updated_at: new Date().toISOString() } : prev);
      setRuntimeCache((prev) => prev ? {
        ...prev,
        manifest: {
          ...(prev.manifest && typeof prev.manifest === "object" && !Array.isArray(prev.manifest) ? prev.manifest : {}),
          name: savedName,
        },
        updated_at: new Date().toISOString(),
      } : prev);
      setRenaming(false);
      toast({ title: "App renamed" });
    } catch (error) {
      toast({
        title: "Rename failed",
        description: error instanceof Error ? error.message : "Unable to rename this App.",
        variant: "destructive",
      });
    } finally {
      setRenameSaving(false);
    }
  };

  const saveDescription = async () => {
    if (!app || !canEditAppMetadata || descriptionSaving) return;
    const nextDescription = normalizeOperatingSystemDescription(descriptionValue);
    if ((nextDescription ?? null) === (appDescription ?? null)) {
      cancelDescriptionEdit();
      return;
    }

    setDescriptionSaving(true);
    try {
      const data = await invokeAppFunction<{ description?: string | null }>("rename-operating-system", {
          operatingSystemId: app.id,
          description: nextDescription,
        
      });
      const result = (data ?? {}) as { description?: string | null };
      const savedDescription = normalizeOperatingSystemDescription(result.description) ?? null;
      setApp((prev) => {
        if (!prev) return prev;
        const prevProperties = prev.properties && typeof prev.properties === "object" && !Array.isArray(prev.properties)
          ? prev.properties as Record<string, unknown>
          : {};
        const nextProperties = { ...prevProperties };
        if (savedDescription) nextProperties.description = savedDescription;
        else delete nextProperties.description;
        return {
          ...prev,
          properties: nextProperties,
          updated_at: new Date().toISOString(),
        };
      });
      setRuntimeCache((prev) => {
        if (!prev) return prev;
        const manifest = prev.manifest && typeof prev.manifest === "object" && !Array.isArray(prev.manifest)
          ? { ...(prev.manifest as Record<string, unknown>) }
          : {};
        if (savedDescription) manifest.description = savedDescription;
        else delete manifest.description;
        return {
          ...prev,
          manifest: manifest as OperatingSystemRuntimeCache["manifest"],
          updated_at: new Date().toISOString(),
        };

      });
      setDescriptionEditing(false);
      toast({ title: savedDescription ? "Description saved" : "Description cleared" });
    } catch (error) {
      toast({
        title: "Description failed",
        description: error instanceof Error ? error.message : "Unable to save this App description.",
        variant: "destructive",
      });
    } finally {
      setDescriptionSaving(false);
    }
  };

  return (
    <div className="flex h-dvh min-h-screen flex-col overflow-hidden bg-background">
      <Header compact />
      <main className="flex flex-1 flex-col min-h-0">
        <div className="relative z-30 border-b border-border bg-background">
          <div className="mx-auto flex w-full max-w-[1680px] flex-wrap items-center gap-2 px-3 py-1 sm:px-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => navigate("/applications")}
              aria-label="Back to My Apps"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {canManageApp && app && canView && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => setEditPanelOpen((open) => !open)}
                  aria-label={editPanelOpen ? "Hide edit panel" : "Show edit panel"}
                  title={editPanelOpen ? "Hide edit panel" : "Show edit panel"}
                >
                  {editPanelOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
                </Button>
                {editPanelOpen && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => void syncHistory({ userInitiated: true })}
                    aria-label="Refresh activity"
                    title="Refresh activity"
                  >
                    {historySyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  </Button>
                )}
              </>
            )}


            {renaming ? (
              <form
                className="flex min-w-[min(100%,14rem)] flex-1 items-center gap-1.5"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveRename();
                }}
              >
                <Input
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault();
                      cancelRename();
                    }
                  }}
                  className="h-8 min-w-0 w-full max-w-2xl flex-1 text-sm font-semibold"
                  maxLength={140}
                  autoFocus
                  aria-label="App name"
                />
                <Button type="submit" size="icon" className="h-8 w-8 shrink-0" disabled={renameSaving} aria-label="Save App name">
                  {renameSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                </Button>
                <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={cancelRename} disabled={renameSaving} aria-label="Cancel rename">
                  <X className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <h1
                  className="truncate text-sm font-semibold leading-6 text-foreground sm:text-base"
                  title={app?.name || undefined}
                >
                  {app && canView ? app.name : "App"}
                </h1>
                {app && canView && canEditAppMetadata && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={startRename}
                    aria-label="Rename App"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
                {app && canView && (
                  <Badge variant="outline" className={`shrink-0 px-2 py-0 text-[11px] leading-5 ${previewUnavailable ? "border-amber-300 bg-amber-50 text-amber-800" : statusBadgeClass(app.status, templateStatus, statusDescriptor?.badgeClass)}`}>
                    {previewUnavailable ? "Unavailable" : statusLabel}
                  </Badge>
                )}
                {app && canView && appDescription && !descriptionEditing && (
                  <span
                    className="hidden min-w-0 max-w-[40vw] truncate text-xs leading-5 text-muted-foreground md:inline"
                    title={appDescription}
                  >
                    {appDescription}
                  </span>
                )}
                {headerDetail && permissionResolved && canView && (
                  <span className="hidden min-w-0 truncate text-xs leading-5 text-muted-foreground xl:inline">
                    {headerDetail}
                  </span>
                )}
              </div>
            )}

            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {canFix && (
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={requestRestart}
                  disabled={retrying || active} aria-label={retrying || active ? "Fixing App" : "Fix App"}>
                  {retrying || active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {retrying || active ? "Fixing..." : "Fix"}
                </Button>
              )}
              {canManualRestart && !canFix && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={requestRestart} disabled={retrying} aria-label={retryLabel}>
                      {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{retryLabel}</TooltipContent>
                </Tooltip>
              )}
              {canEditAppMetadata && app && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  onClick={startDescriptionEdit}
                  aria-label={appDescription ? "Edit description" : "Add description"}
                >
                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>Description</span>
                </Button>
              )}
              {app && canView && (
                <div ref={moreActionsRef} className="relative">
                    <button
                      ref={moreActionsTriggerRef}
                      type="button"
                      className="inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onPointerDown={(event) => {
                        if (event.button !== 0) return;
                        event.preventDefault();
                        setMoreActionsOpen((open) => !open);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setMoreActionsOpen((open) => !open);
                          return;
                        }
                        if (event.key !== "ArrowDown") return;
                        event.preventDefault();
                        setMoreActionsOpen(true);
                        window.requestAnimationFrame(() => {
                          moreActionsMenuRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
                        });
                      }}
                      aria-label="More App actions"
                      aria-haspopup="menu"
                      aria-expanded={moreActionsOpen}
                      aria-controls="more-app-actions-menu"
                    >
                      <MoreHorizontal className="pointer-events-none h-3.5 w-3.5" aria-hidden="true" />
                      Actions
                    </button>
                    {moreActionsOpen && (
                      <div
                        ref={moreActionsMenuRef}
                        id="more-app-actions-menu"
                        role="menu"
                        aria-label="More App actions"
                        className="absolute right-0 top-[calc(100%+6px)] z-[100] w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
                      >
                    {canOpenRunPage && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          openRunPage();
                        }}
                        aria-label={`Open ${app.name || "App"} full screen in a new tab`}
                      >
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        Open fullscreen
                      </button>
                    )}
                    {canManageApp && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          setIntegrationsOpen(true);
                        }}
                        aria-label={integrationAttentionCount === null
                          ? "Open integrations"
                          : `Open integrations, ${integrationAttentionCount} need attention`}
                      >
                        <Plug className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                        Integrations
                        {integrationAttentionCount !== null && integrationAttentionCount > 0 && (
                          <Badge className="ml-auto h-4 min-w-4 rounded-full px-1 text-[10px] leading-4" aria-label={`${integrationAttentionCount} integrations need attention`}>
                            {integrationAttentionCount}
                          </Badge>
                        )}
                      </button>
                    )}
                    {canManageApp && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          void loadOperatingSystem();
                        }}
                        disabled={initialLoading}
                      >
                        <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        Refresh App
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                      onClick={() => {
                        setMoreActionsOpen(false);
                        void downloadDocumentation();
                      }}
                      disabled={documentationDownloading || documentPdfPreparing || !app.source_message_id || !documentPdf?.pdfUrl}
                    >
                      <Download className="mr-2 h-3.5 w-3.5" />
                      Download documentation
                    </button>
                    {canConvertToTemplate && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          setTemplateConversionOpen(true);
                        }}
                        disabled={templateConverting}
                      >
                        <LayoutTemplate className="mr-2 h-3.5 w-3.5" />
                        Create Template
                      </button>
                    )}
                    {canManageSharing && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          setShareOpen(true);
                        }}
                      >
                        <Share2 className="mr-2 h-3.5 w-3.5" />
                        Share App
                      </button>
                    )}
                    {canManageApp && (
                      <button
                        type="button"
                        role="menuitem"
                        className="relative flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm text-destructive outline-none transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:text-destructive disabled:pointer-events-none disabled:opacity-50"
                        onClick={() => {
                          setMoreActionsOpen(false);
                          setDeleteConfirmOpen(true);
                        }}
                        disabled={deleting}
                      >
                        <Trash2 className="mr-2 h-3.5 w-3.5" />
                        Delete App
                      </button>
                    )}
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>

          {app && canView && statusDescriptor?.availabilityMessage &&
            runtimeAvailable && (
            <p className="mx-auto w-full max-w-[1680px] px-3 pb-2 text-xs text-muted-foreground sm:px-4" role="status">{statusDescriptor.availabilityMessage}</p>
          )}

          {app && canView && descriptionEditing && (
            <form
              className="mx-auto flex w-full max-w-[1680px] items-start gap-1.5 px-3 pb-2 sm:px-4"
              onSubmit={(event) => {
                event.preventDefault();
                void saveDescription();
              }}
            >
              <Textarea
                value={descriptionValue}
                onChange={(event) => setDescriptionValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.preventDefault();
                    cancelDescriptionEdit();
                  }
                  if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                    event.preventDefault();
                    void saveDescription();
                  }
                }}
                className="min-h-[52px] flex-1 resize-none text-sm font-medium leading-5"
                maxLength={OPERATING_SYSTEM_DESCRIPTION_MAX_LENGTH}
                autoFocus
                aria-label="App description"
              />
              <Button type="submit" size="icon" className="h-8 w-8 shrink-0" disabled={descriptionSaving} aria-label="Save App description">
                {descriptionSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={cancelDescriptionEdit} disabled={descriptionSaving} aria-label="Cancel description edit">
                <X className="h-4 w-4" />
              </Button>
            </form>
          )}
          {active && !quotaPaused && permissionResolved && canView && (
            <div className="mx-auto w-full max-w-[1680px] px-4 pb-1 sm:px-6">
              <Progress value={progress} className="h-1" />
            </div>

          )}
          {quotaPaused && permissionResolved && canManageApp && (
            <div className="mx-auto flex w-full max-w-[1680px] flex-wrap items-center justify-between gap-3 px-4 pb-3 sm:px-6" role="status" aria-live="polite">
              <p className="max-w-3xl text-sm text-muted-foreground">
                Update paused: provider message quota reached. We’ll resume automatically when quota is available.
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => void runQuotaAction("check_again")} disabled={!!quotaAction}>
                  {quotaAction === "check_again" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  Check again
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void runQuotaAction("cancel_update")} disabled={!!quotaAction}>
                  {quotaAction === "cancel_update" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  Cancel update
                </Button>
              </div>
            </div>
          )}
          {stuckUpdate && permissionResolved && (
            <div className="mx-auto flex w-full max-w-[1680px] flex-wrap items-center justify-between gap-3 px-4 pb-3 sm:px-6" role="status">
              <p className="max-w-3xl text-sm text-muted-foreground">
                {stopBeforeSend
                  ? "This update is taking longer than expected. We can verify and keep your last working version."
                  : "This update is taking longer than expected. Check its progress again shortly."}
              </p>
              <div className="flex shrink-0 items-center gap-2">
                {stopBeforeSend ? (
                  <Button variant="outline" size="sm" onClick={() => setStopUpdateConfirmOpen(true)} disabled={!!stuckUpdateAction}>
                    Stop Update &amp; Keep Working Version
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => void recoverStuckUpdate("check_now")} disabled={!!stuckUpdateAction}>
                    {stuckUpdateAction === "check_now" && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                    Check update now
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {!authLoading && (!user || isAnonymous) ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="text-center">
              <Rocket className="mx-auto h-10 w-10 text-primary" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">Sign in to view this App</h2>
              <Button className="mt-5" onClick={() => navigate("/auth")}>Sign in</Button>
            </div>
          </div>
        ) : initialLoading && !app ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading App...
          </div>
        ) : loadError && !app ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center" role="alert">
            <h2 className="text-lg font-semibold">Unable to load App</h2>
            <p className="text-sm text-muted-foreground">Please try loading it again.</p>
            <Button onClick={() => void loadOperatingSystem()}>Retry</Button>
          </div>
        ) : !app ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="text-center">
              <Rocket className="mx-auto h-10 w-10 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">App not found</h2>
              <Button className="mt-5" onClick={() => navigate("/applications")}>Back to list</Button>
            </div>
          </div>
        ) : !permissionResolved ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Checking access...
          </div>
        ) : !canView ? (
          <div className="flex flex-1 items-center justify-center px-4">
            <div className="text-center">
              <Rocket className="mx-auto h-10 w-10 text-muted-foreground" />
              <h2 className="mt-4 text-lg font-semibold text-foreground">App not found</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                It may have been removed, or you do not have access to it.
              </p>
              <Button className="mt-5" onClick={() => navigate("/applications")}>Back to list</Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            {loadError && <div role="status" className="border-b px-4 py-2 text-sm">Could not refresh App status. Showing the last loaded state. <Button variant="link" onClick={() => void loadOperatingSystem()}>Retry</Button></div>}
            {canViewSourceExport && sourceExportStatus?.app_id === app?.id && (sourceExportStatus.status === "failed" || sourceExportStatus.source_unmanaged) && (
              <div role="alert" className="border-b border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
                {sourceExportStatus.source_unmanaged
                  ? "This code-only App cannot yet be converted to a complete Template. Your App is unchanged."
                  : "Template preparation failed. This App is unchanged. You can try Create Template again, or contact support if it keeps failing."}
              </div>
            )}
            <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            {canManageApp && editPanelOpen ? (
              <RecoveryBoundary scope="panel" onClose={() => setEditPanelOpen(false)}>
              <Base44MessagesPanel
                statusDescriptor={statusDescriptor ?? undefined}
                app={app}
                job={job}
                active={active}
                progress={progress}
                historyEvents={panelHistoryEvents}
                historyLoading={historyLoading}
                historySyncing={historySyncing}
                historyError={historyError}
                changeRequest={changeRequest}
                attachments={changeAttachments}
                onAttachmentsChange={setChangeAttachments}
                changeError={changeError}
                submitting={independentMessaging ? false : changeSubmitting}
                independentMessaging={independentMessaging}
                canApply={independentMessaging ? canManageApp && messageTransport?.connected === true : canApplyChange}
                canApplyRouterClarification={canApplyChange || canAnswerActiveRouterClarification}
                canAnswerProviderClarification={canAnswerProviderClarification}
                providerInputWaiting={providerInputWaiting}
                unavailableReason={independentMessaging ? (messageTransport?.connected ? null : "This App has no connected application.") : changeUnavailableReason}
                onHide={() => setEditPanelOpen(false)}
                onRefresh={() => void syncHistory({ userInitiated: true })}
                onChangeRequest={updateChangeRequest}
                onOpenIntegrations={() => setIntegrationsOpen(true)}
                onClarifyingAnswer={submitClarifyingAnswer}
                onProviderDialogLoad={loadProviderDialog}
                onProviderDialogAction={submitProviderDialogAction}
                onProviderUiAction={submitProviderUiAction}
                onRetryAuthorization={retryInterruptedAuthorization}
                onProviderMessageReply={submitProviderMessageReply}
                onRouterClarificationAnswer={submitRouterClarifyingAnswer}
                onSubmit={submitChangeRequest}
                loadAgenticFlowList={loadAgenticFlowList}
                loadAgenticFlowDetail={loadAgenticFlowDetail}
                loadAgenticFlowDebug={loadAgenticFlowDebug}
                runAgenticFlow={runAgenticFlow}
                recurrenceAction={recurrenceAction}
                checkAgenticSetup={checkAgenticSetup}
                mutateAgenticFlow={mutateAgenticFlow}
                isPlatformAdmin={isPlatformAdmin}
                onDiscoverAgenticFlows={() => {
                  if (isAgenticUiAvailableHere()) void queueChangeRequest("What useful recurring work could this App handle?");
                }}
                onAddAgenticFlow={(prompt, reference) => isAgenticUiAvailableHere() && void queueChangeRequest(prompt, {}, {
                  type: "agentic_flow_add",
                  discoveryId: reference.discoveryId,
                  candidateId: reference.candidateId,
                  displayName: reference.displayName,
                })}
                onTargetAgenticFlowModification={(flowId, displayName) => {
                  if (!isAgenticUiAvailableHere()) return;
                  setAgenticFlowTarget({ id: flowId, name: displayName });
                  updateChangeRequest(`Change the execution plan “${displayName}”: `);
                }}
              />
              </RecoveryBoundary>
            ) : canManageApp ? (
              <div
                className="flex h-12 shrink-0 items-center border-b border-[#d7dce4] bg-[#f7f8fb] px-3 lg:h-auto lg:w-14 lg:flex-col lg:border-b-0 lg:border-r lg:px-0 lg:py-3"
                aria-label="Edit panel controls"
              >
                <button
                  type="button"
                  onClick={() => setEditPanelOpen(true)}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-slate-700 hover:bg-slate-200/80"
                  aria-label="Show edit panel"
                  title="Show edit panel"
                >
                  <PanelLeftOpen className="h-4 w-4" />
                </button>
              </div>
            ) : null}
            {isTemplateSnapshot ? (
              <div className="flex min-h-[70vh] flex-1 items-center justify-center bg-muted/30 p-6">
                {templateSnapshotImageUrl ? (
                  <img src={templateSnapshotImageUrl} alt={`Static Template preview of ${app.name}`}
                    className="max-h-[70vh] max-w-full rounded-lg border bg-white object-contain" />
                ) : (
                  <p className="text-sm text-muted-foreground">Preview image</p>
                )}
              </div>
            ) : previewDescriptor && openUrl ? (
              <div className="flex min-h-0 flex-1 flex-col bg-white">
                {previewNotice && (
                  <div className="flex min-h-8 items-center border-b border-border bg-muted/30 px-4 py-1 text-[11px] text-muted-foreground">
                    <span className="min-w-0 flex-1 truncate">{previewNotice}</span>
                  </div>
                )}
                <div
                  ref={setPreviewTarget}
                  className="relative min-h-[70vh] flex-1 bg-white"
                  aria-busy={!previewFrameVisible && !showPreviewFailure}
                >
                  {showPreviewFailure ? (
                    <div className="absolute inset-0 z-20 flex items-center justify-center overflow-hidden bg-background/95 px-4">
                      <div role="alert" className="relative w-full max-w-md rounded-lg border border-border bg-background p-6 text-center shadow-sm">
                        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                          <RefreshCw className="h-5 w-5" />
                        </div>
                        <h2 className="mt-4 text-base font-semibold text-foreground">{previewUnavailable ? "Live workspace unavailable" : "Live workspace did not render"}</h2>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {previewUnavailable
                            ? "The saved workspace could not be opened. Try reconnecting. If this continues, contact support to check workspace access."
                            : "Applyt retried the live connection, but the App has not shown visible content. Try reconnecting, or contact support if this continues."}
                        </p>
                        <Button className="mt-4" onClick={previewRecovery.retry} disabled={previewRecovery.retrying}>
                          <RefreshCw className="mr-2 h-4 w-4" />Retry connection
                        </Button>
                      </div>
                    </div>
                  ) : showPreviewConnecting ? (
                    <div className="absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-background px-4">
                      <LiveWorkspaceConnectingOverlay
                        appName={app.name}
                        hasSnapshot={false}
                        providerLoading={previewProviderLoading}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center px-4">
                <div className="w-full max-w-md rounded-lg border border-border p-6 text-center">
                  {runtimeAvailable && proxyTicketLoading ? (
                    <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
                  ) : (
                    <Rocket className="mx-auto h-10 w-10 text-primary" />
                  )}
                  <h2 className="mt-4 text-lg font-semibold text-foreground">
                    {runtimeAvailable && proxyTicketLoading
                      ? "Opening secure runtime"
                      : runtimeAvailable && proxyTicketError
                        ? "Runtime proxy unavailable"
                      : app.status === "ready" ? "Workspace unavailable" : statusLabel || getOperatingSystemStatusLabel(app.status)}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {runtimeAvailable && proxyTicketError
                      ? proxyTicketError
                      : runtimeAvailable && proxyTicketLoading
                        ? "Applyt is issuing a short-lived runtime URL."
                        : app.status === "ready"
                          ? "This App has no workspace available to open. Contact support to check its runtime connection."
                          : feedback || "Your App is being prepared."}
                  </p>
                  {active && !supportManagedRecovery && <Progress value={progress} className="mt-5 h-2" />}
                  {runtimeAvailable && !previewDescriptor && (
                    <Button className="mt-5" onClick={previewRecovery.retry} disabled={previewRecovery.retrying}>
                      <RefreshCw className="mr-2 h-4 w-4" />Retry connection
                    </Button>
                  )}
                  {(canFix || canManualRestart) && (
                    <Button className="mt-5" onClick={requestRestart} disabled={retrying || (canFix && active)}>
                      {retrying || (canFix && active) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                      {canFix ? retrying || active ? "Fixing..." : "Fix" : retryLabel}
                    </Button>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </main>
      <AlertDialog open={stopUpdateConfirmOpen} onOpenChange={(open) => {
        if (!stuckUpdateAction) setStopUpdateConfirmOpen(open);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this update?</AlertDialogTitle>
            <AlertDialogDescription>
              We’ll keep the last working App version. Changes from this unfinished update may be lost. If changes have already reached the live App, we’ll refuse to stop it and ask you to check its status instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!stuckUpdateAction}>Keep updating</AlertDialogCancel>
            <AlertDialogAction disabled={!!stuckUpdateAction} onClick={(event) => {
              event.preventDefault();
              void recoverStuckUpdate("stop");
            }}>
              {stuckUpdateAction === "stop" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Stop Update &amp; Keep Working Version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={restartConfirmOpen} onOpenChange={(open) => {
        if (!open && !retrying && !restartSubmittingRef.current) setRestartConfirmOpen(false);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{retryLabel}?</AlertDialogTitle>
            <AlertDialogDescription>{restartDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retrying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={retrying}
              onClick={(event) => {
                event.preventDefault();
                void confirmRestart();
              }}
            >
              {retrying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {retryLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={templateConversionOpen} onOpenChange={(open) => {
        if (!open && !templateConverting) setTemplateConversionOpen(false);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create a private Template snapshot?</AlertDialogTitle>
            <AlertDialogDescription>
              A private copy of the structure and data in "{app?.name ?? "this App"}" will be saved.
              Your original App stays available, and later edits will not change this Template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={templateConverting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={templateConverting}
              onClick={(event) => {
                event.preventDefault();
                void convertToTemplate();
              }}
            >
              {templateConverting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={deleteConfirmOpen} onOpenChange={(open) => {
        if (!open && !deleting) setDeleteConfirmOpen(false);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this App?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes "{app?.name ?? "this App"}", its job history, cached runtime details, and screenshots.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                void deleteOperatingSystem();
              }}
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete App
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {app && (
        <ShareOperatingSystemDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          operatingSystemId={app.id}
        />
      )}
      {app && canManageApp && (
        <AppIntegrationsDrawer
          open={integrationsOpen}
          onOpenChange={setIntegrationsOpen}
          operatingSystemId={app.id}
          updatesActive={active}
          appPrepared={hasOperatingSystemEditableRuntime(app)}
          onStartAppUpdate={(request, appAction) => queueChangeRequest(request, {}, appAction)}
          triggerRef={moreActionsTriggerRef}
          enabled={permissionResolved && canManageApp}
          onSummary={handleIntegrationSummary}
        />
      )}
    </div>
  );
}
